import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type Konva from 'konva';

import { getEdgeGeometry } from './utils/edgeMath';
import { useDiagramStore, setDiagramPersistenceEnabled } from './stores/diagramStore';
import { useSettingsStore } from './stores/settingsStore';
import { useUIStore } from './stores/uiStore';
import { NODE_TYPES, CANVAS, STATUS_COLORS, NODE, SEQUENCE, FONT_FAMILY } from './constants';
import { GENERAL_SHAPE_TYPES, UML_CLASSIFIER_TYPES, isMarkupType, isMagneticNode, locksEqualAspect, magneticMinSize } from './constants/nodeTypes';
import { getKit, kitQuickCreateTypes, resolveRankAxis } from './utils/diagramKits';
import { withStickyDrawColor } from './utils/colorPresets';
import { MAGNETIC_SOCKET_SNAP_PX, nearestSocket, pickMagneticTargetSocket } from './utils/magneticConnect';
import { arrowFromPoints, fitTextNodeSize, isStrokeMarkup } from './utils/markup';
import { usesWrappedCenteredLabel, constrainedResize } from './utils/sketchLabel';
import { findStackParent, getStackParentId, getStackSnap, isStackableDrawing, collectStackDescendantIds, collectSnappedAncestorPatches, centeredResize, detectStackSnap, snappedPosition } from './utils/drawStack';
import { DESC_H, calcNodeHeight, buildSocketsForDirection, buildFourSidedSockets, buildTriangleSocketsRotated, buildHexagonSocketsRotated, projectToGeneralShapeBoundary, rebuildSketchSockets, scaleSocketsToSize, socketsMatchFourSided } from './utils/socketLayout';
import { ColorPickerModal, ContextMenu, MermaidModal, DevStateViewer, ProjectManager, ChordReferenceModal, PngExportModal } from './components/modals';
import { useProjectStore } from './stores/projectStore';
import { StatusBar, SettingsPanel, FeedbackPanel, FragmentInfoPanel, MessageInfoPanel, ParticipantInfoPanel, AIChatPanel } from './components/panels';
import { ChordHint, ConnectionHint, FrameDrawHint, DiscoveryOverlay, GroupBreadcrumb, EmptyState, CommandPalette, ShortcutNudge, TutorialCoach } from './components/overlays';
import { UmlMemberEditor } from './components/panels/UmlMemberEditor';
import { classifierBoxSize } from './utils/umlMembers';
import { InPlaceSketchLabel } from './components/overlays/InPlaceSketchLabel';
import { usedViaPointer, usedViaPointerNodeType, usedViaKeyboard, useOnboardingStore } from './onboarding';
import { saveToJSON, loadFromJSON } from './utils/serializer';
import { decodePublishedHash } from './utils/publishCodec';
import { createInteractionPolicy } from './utils/interactionPolicy';
import {
  NodeMeta,
  Node as DiagramNode,
  Point,
  SequenceMessage,
  KonvaMouseEvent,
  AppMode,
  ContextMenuState,
} from './types';
import { sanitizeFileStem, generateId } from './utils';
import { countCatalogViews } from './utils/catalog';
import { diagramToMermaid, diagramToMermaidSequence } from './utils/mermaidExchange';
import { computeDiagramContentBounds, computeDynamicMinScale, computeLifelineBottom } from './utils/contentBounds';
import {
  computeStickyHeaderScreenHeight,
  computeStickyMaskBandScreenRects,
  resolveStickyMode,
  screenToCanvasY,
} from './utils/sequenceSticky';
import {
  SequenceMessageRowSnapshot,
} from './utils/sequenceMessageLayout';
import { resolveSequenceAppearanceMode, resolveSequencePalette } from './utils/sequenceAppearance';
import { useUndoStore, snapshotFromStore } from './stores/undoStore';
import { PageManager } from './components/PageManager';
import { useAppKeyboard } from './hooks/useAppKeyboard';
import { useCanvasHandlers } from './hooks/useCanvasHandlers';
import { useMultiDrag } from './hooks/useMultiDrag';
import { useWaypointDrag } from './hooks/useWaypointDrag';
import { usePersistence } from './hooks/usePersistence';
import { usePageNavigation } from './hooks/usePageNavigation';
import { useSequenceDrag } from './hooks/useSequenceDrag';
import { useSequenceMessageLayout } from './hooks/useSequenceMessageLayout';
import { useNodeInfoPanel } from './hooks/useNodeInfoPanel';
import { useFlowLayerVisibility } from './hooks/useFlowLayerVisibility';
import { useProxySocketMap } from './hooks/useProxySocketMap';
import { useVelostructBridge } from './hooks/useVelostructBridge';
import { useMcpDirectClient } from './hooks/useMcpDirectClient';
import { invoke, isCommandError } from './agent';
import { useConnectionGeometry } from './hooks/useConnectionGeometry';
import { useGroupViewFilter } from './hooks/useGroupViewFilter';
import { useDiagramExport } from './hooks/useDiagramExport';
import { usePublishLink } from './hooks/usePublishLink';
import { NodeFactory } from './services/node/NodeFactory';
import { LayoutService } from './services/layout';
import { FlowCanvas } from './components/canvas/FlowCanvas';
import { CanvasStage } from './components/canvas/CanvasStage';
import { TopToolbar } from './components/toolbar/TopToolbar';
import { LeftSidebar } from './components/sidebar/LeftSidebar';
import { LayerFlowPanel } from './components/sidebar/LayerFlowPanel';
import { SequenceCanvas } from './components/sequence/SequenceCanvas';
import type { SequenceCanvasRefs, SequenceCanvasCallbacks, SequenceCanvasDragCallbacks, SequenceCanvasMessageDragCallbacks } from './components/sequence/SequenceCanvas';

const NODE_WIDTH = NODE.DEFAULT_WIDTH;

const SEQUENCE_STICKY_TOP = SEQUENCE.STICKY_TOP;
const SEQUENCE_STICKY_TOP_WITH_HEADER = SEQUENCE.STICKY_TOP_WITH_HEADER;
const SEQUENCE_HEADER_Y = SEQUENCE.HEADER_Y;
const SEQUENCE_HEADER_HEIGHT = SEQUENCE.HEADER_HEIGHT;
const SEQUENCE_STICKY_BOTTOM_INSET = SEQUENCE.STICKY_BOTTOM_INSET;

interface AppProps {
  mode?: AppMode;
}

function App({ mode = 'edit' }: AppProps) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const {
    isToolboxOpen,
    setToolboxOpen: setIsToolboxOpen,
    sidebarTab,
    setSidebarTab,
    hoveredNodeId,
    setHoveredNodeId,
    hoveredParticipantId,
    setHoveredParticipantId,
    hoveredMessageId,
    setHoveredMessageId,
    isJsonPanelOpen,
    setJsonPanelOpen: setIsJsonPanelOpen,
    isLayerPanelOpen,
    setLayerPanelOpen: setIsLayerPanelOpen,
    isSettingsOpen,
    setSettingsOpen: setIsSettingsOpen,
    isFeedbackOpen,
    setFeedbackOpen: setIsFeedbackOpen,
    isDevStateViewerOpen,
    setDevStateViewerOpen: setIsDevStateViewerOpen,
    isProjectManagerOpen,
    setProjectManagerOpen: setIsProjectManagerOpen,
    isChordModalOpen,
    setChordModalOpen: setIsChordModalOpen,
    isPngExportModalOpen,
    setPngExportModalOpen: setIsPngExportModalOpen,
    isMermaidModalOpen,
    setMermaidModalOpen: setIsMermaidModalOpen,
    isPageManagerOpen,
    setPageManagerOpen: setIsPageManagerOpen,
    isDistractionFree,
    setDistractionFree: setIsDistractionFree,
    isHeaderVisible,
    setHeaderVisible: setIsHeaderVisible,
    syncHeaderForProject,
    revealHeader,
    isToolbarExpanded,
    setToolbarExpanded: setIsToolbarExpanded,
    openDropdown,
    setOpenDropdown,
    infoPanelNodeId,
    setInfoPanelNodeId,
    infoPanelParticipantId,
    setInfoPanelParticipantId,
    infoPanelMessageId,
    setInfoPanelMessageId,
    infoPanelFragmentId,
    setInfoPanelFragmentId,
    isFrameDrawMode,
    setFrameDrawMode: setIsFrameDrawMode,
    canvasDrawTool,
    setCanvasDrawTool,
    canvasStampType,
    setCanvasStampType,
    canvasConnectMode,
    discoveryMode,
    setDiscoveryMode,
  } = useUIStore();
  const [emptyStateDismissed, setEmptyStateDismissed] = useState(false);
  const [renamingPageId, setRenamingPageId] = useState<string | null>(null);
  const [renamingPageVal, setRenamingPageVal] = useState('');
  const [toolbarCollapseTimer, setToolbarCollapseTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const [locationHash, setLocationHash] = useState(window.location.hash);
  const publishedFromHash = useMemo(() => decodePublishedHash(locationHash), [locationHash]);
  const isViewOnly = mode === 'view' || !!publishedFromHash;
  const showLocalDevTools = import.meta.env.DEV;
  const interactionPolicy = useMemo(() => createInteractionPolicy(isViewOnly), [isViewOnly]);

  useEffect(() => {
    if (mode === 'view') {
      setSidebarTab('info');
    }
  }, [mode, setSidebarTab]);

  // Toast notification
  const [toast, setToast] = useState<{ message: string; id: number } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [seqMsgTooltip, setSeqMsgTooltip] = useState<{ msgId: string; screenX: number; screenY: number } | null>(null);

  // Inline editing overlay (label or description, positioned over canvas)
  const [inlineEdit, setInlineEdit] = useState<{ nodeId: string; field: 'label' | 'description' } | null>(null);

  // Inline edge label editing (msgId present = editing existing message; absent = adding new)
  const [inlineEdgeEdit, setInlineEdgeEdit] = useState<{
    edgeId: string;
    msgId?: string;
    canvasCX?: number; canvasCY?: number;
    canvasBoxW?: number; canvasBoxH?: number;
  } | null>(null);

  // Inline group/frame label editing
  const [inlineGroupEdit, setInlineGroupEdit] = useState<{ groupId: string } | null>(null);

  // Inline socket naming (after adding a new socket)
  const [inlineSocketEdit, setInlineSocketEdit] = useState<{ nodeId: string; socketId: string } | null>(null);

  // Socket slide: Alt+drag to reposition a socket along the shape boundary
  const slidingSocketRef = useRef<{ nodeId: string; socketId: string } | null>(null);

  // Tooltip (hover + I key)
  const [tooltip, setTooltip] = useState<{ screenX: number; screenY: number; nodeId: string } | null>(null);

  // Cursor tracking for tooltip hit-test
  const cursorCanvasPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const discoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-edit after add: set true before addNode, useEffect opens edit for the new node
  const pendingEditAfterAdd = useRef(false);
  const prevNodesLen = useRef(0);

  // Inline layer rename — set after + click, cleared on blur/enter/esc
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const pendingLayerEdit = useRef(false);
  const prevLayersLen = useRef(0);

  // Layers/Flows panel tab
  const [bottomPanelTab, setBottomPanelTab] = useState<'layers' | 'flows'>('layers');
  // Active flow — selected flow ID; null = no flow active (show all)
  const [activeFlowId, setActiveFlowId] = useState<string | null>(null);
  const [editingFlowId, setEditingFlowId] = useState<string | null>(null);


  // Sequence drag state for message creation
  const [sequenceDrag, setSequenceDrag] = useState<{
    sourceParticipantId: string;
    sourceX: number; // lifeline center X
    startY: number;
    currentX?: number;
  } | null>(null);

  // Stores the Y of a newly-added message so we can auto-open its inline edit
  const [pendingEditY, setPendingEditY] = useState<number | null>(null);

  // Sequence message clipboard
  const [seqClipboard, setSeqClipboard] = useState<SequenceMessage | null>(null);
  const [isCut, setIsCut] = useState(false);

  // Fragment nesting hint: ID of the fragment that would become a parent during a drag
  const [fragmentDropParentId, setFragmentDropParentId] = useState<string | null>(null);

  const [linkPageForNodeId, setLinkPageForNodeId] = useState<string | null>(null);

  // Quick node creation during edge drag
  const [quickNodeCreation, setQuickNodeCreation] = useState<{ show: boolean; screenX: number; screenY: number } | null>(null);

  // Anchor node indicator — subtle ring on the node new chords will connect from
  const [anchorNodeId, setAnchorNodeId] = useState<string | null>(null);

  // R key ref — owned here so it can be passed to both useAppKeyboard and useWaypointDrag
  const rKeyHeld = useRef(false);
  // M key ref — held to move existing edges instead of creating new ones
  const mKeyHeld = useRef(false);
  // Tracks the edge being moved in M-mode so mouseUp can retarget it
  const pendingEdgeMoveRef = useRef<{ edgeId: string; anchorNodeId: string; anchorSocketId: string; movingSource: boolean } | null>(null);

  // Track mouse position for smart node placement
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);
  const lastNodeAddPos = useRef<{ x: number; y: number } | null>(null);
  const lastChordAddCanvasPos = useRef<{ x: number; y: number } | null>(null);

  // Multi-select drag — managed by useMultiDrag (initialised below after checkFrameMembership)

  // Color picker state — targetNodeIds supports hover target, single, or multi-selection
  const [colorPicker, setColorPicker] = useState<{ show: boolean; targetNodeIds: string[]; targetGroupIds?: string[]; targetEdgeIds?: string[]; targetParticipantIds?: string[]; targetMessageIds?: string[]; avoidRect?: { x: number; y: number; w: number; h: number } } | null>(null);

  // Pan state
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<{ sx: number; sy: number; screenX: number; screenY: number; vpX: number; vpY: number } | null>(null);
  const leftPanPending = useRef<{ sx: number; sy: number; screenX: number; screenY: number; vpX: number; vpY: number } | null>(null);
  const canvasMouseDownPos = useRef<{ x: number; y: number } | null>(null);

  // Marquee selection
  const [marqueeRect, setMarqueeRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const marqueeStart = useRef<{ x: number; y: number } | null>(null);
  const [lassoPoints, setLassoPoints] = useState<number[] | null>(null);

  const [frameDrawRect, setFrameDrawRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [markupStroke, setMarkupStroke] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const frameDrawStart = useRef<{ x: number; y: number } | null>(null);
  const bKeyHeld = useRef(false);  // true when frame mode was activated by holding B
  const eKeyHeld = useRef(false);  // hold E to connect from a node body

  // Keyboard chord macros
  const chordBuffer = useRef<string>('');
  const chordTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [chordHint, setChordHint] = useState<string>('');
  const lastAddedNodeId = useRef<string | null>(null);

  // Track active message drag to snapshot only on start
  const isDraggingMessageRef = useRef<string | null>(null);
  const dragStartMessageYRef = useRef<{ id: string; y: number } | null>(null);
  const dragReorderSnapshotRef = useRef<SequenceMessageRowSnapshot | null>(null);
  const dragSelectedMessageIdsRef = useRef<string[]>([]);
  const reorderMessageDragKeyRef = useRef(false);
  const isDraggingParticipantRef = useRef<string | null>(null);
  // Track active fragment drag to snapshot only on start
  const isDraggingFragmentRef = useRef<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const settingsFileRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const inlineEditInputRef = useRef<HTMLInputElement>(null);
  const inlineEditTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Settings store
  const {
    theme,
    iconStyle,
    colorPickerStyle,
    autoOpenInfoOnHover,
    socketLabelVisibility,
    defaultRoutingMode,
    forceLayoutEdges,
    tintEdgesByDestination,
    layoutDirection,
    mermaidSubgraphMode,
    mermaidNodeStyle,
    generalShapesEnabled,
    stickyDrawColorEnabled,
    lastDrawColor,
    sequenceMessageNumbering,
    sequenceMonochromeViewer,
    sequenceMonochromePaper,
    palette,
    setTheme,
    setIconStyle,
    setColorPickerStyle,
    setAutoOpenInfoOnHover,
    setSocketLabelVisibility,
    setDefaultRoutingMode,
    setForceLayoutEdges,
    setTintEdgesByDestination,
    setLayoutDirection,
    setMermaidSubgraphMode,
    setMermaidNodeStyle,
    setGeneralShapesEnabled,
    setStickyDrawColorEnabled,
    setSequenceMessageNumbering,
    setSequenceMonochromeViewer,
    setSequenceMonochromePaper,
    exportSettings,
    importSettings,
  } = useSettingsStore();

  const {
    diagramType, participants, messages, fragments, participantGroups,
    seqLifelineLength,
    setDiagramType, setSeqLifelineLength, updateParticipant, removeParticipant, reorderParticipants, addParticipant,
    addMessage, updateMessage, applySequenceLayoutPatch, removeMessage, addFragment, updateFragment, moveFragment, removeFragment,
    groupParticipants, ungroupParticipants, toggleParticipantGroupCollapse, updateParticipantGroup, setParticipantLayer,
    nodes, edges, viewport, layers, activeLayerId, groups,
    pages, activePage, groupNavStack,
    addNode, updateNode, removeNode, removeEdge, updateEdge,
    addSocket, renameSocket, moveSocket, loadDiagram, clearDiagram,
    connectionState, startConnection, updateTempEdge, completeConnection,
    getConnectedSockets, selectItem, clearSelection, selectedItems,
    updateViewport,
    addLayer, removeLayer, updateLayer, setNodeLayer, setActiveLayer,
    updateNodeMeta,
    ungroupNodes, toggleGroupCollapse, updateGroup, recalcGroupBounds, createFrame,
    addNodeToGroup, removeNodeFromGroup, addGroupToFrame, removeGroupFromFrame,
    enterGroup,
    // New subgraph-based grouping
    createGroup, dissolveGroup, enterGraph, exitGraph, graphNavStack, graphs,
    catalog, createChildView,
    flows, addFlow, removeFlow, renameFlow, updateFlow,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addPage: _addPage, switchPage, renamePage: _renamePage, deletePage: _deletePage, setPageKit, setPageSettings, linkNodeToPage: _linkNodeToPage, unlinkNodePage: _unlinkNodePage,
  } = useDiagramStore();
  const { loadFromStorage: loadProjects, saveActiveProject, activeProjectId, createProject, projects } = useProjectStore();
  const activeProject = useMemo(
    () => projects.find(project => project.id === activeProjectId) ?? null,
    [projects, activeProjectId],
  );
  const activeProjectName = isViewOnly
    ? (publishedFromHash?.name?.trim() || 'Published Diagram')
    : (activeProject?.name?.trim() || 'diagram');
  const projectFileStem = useMemo(() => sanitizeFileStem(activeProjectName), [activeProjectName]);
  const sequenceLifelineBottom = useMemo(
    () => computeLifelineBottom(messages, fragments, seqLifelineLength),
    [messages, fragments, seqLifelineLength],
  );
  const canvasPalette = useMemo(() => {
    if (diagramType !== 'sequence') return palette;
    return resolveSequencePalette(
      resolveSequenceAppearanceMode({
        viewerEnabled: sequenceMonochromeViewer,
        paper: sequenceMonochromePaper,
      }),
      palette,
    );
  }, [diagramType, palette, sequenceMonochromePaper, sequenceMonochromeViewer]);
  const dynamicMinScale = useMemo(() => {
    const bounds = computeDiagramContentBounds({
      nodes,
      edges,
      groups,
      participants,
      messages,
      fragments,
      seqLifelineLength,
    });
    return computeDynamicMinScale(bounds, windowSize.width, windowSize.height, CANVAS.SCALE_MIN);
  }, [
    nodes,
    edges,
    groups,
    participants,
    messages,
    fragments,
    seqLifelineLength,
    windowSize.width,
    windowSize.height,
  ]);
  const activePageEntry = pages.find(p => p.id === activePage);
  const activeKitId = activePageEntry?.kit;
  /**
   * Page overrides sit between the app defaults and an item's own settings. A
   * page that has chosen nothing simply reads as the app default, and an edge
   * carrying its own `routingMode` still beats whatever the page says.
   */
  const pageSettings = activePageEntry?.settings;
  const pageRoutingMode = pageSettings?.routingMode ?? defaultRoutingMode;
  const pageTextScale = pageSettings?.textScale ?? 1;
  const activeKit = getKit(diagramType === 'sequence' ? null : activeKitId);
  const stickyTopOffset = isHeaderVisible && !isDistractionFree ? SEQUENCE_STICKY_TOP_WITH_HEADER : SEQUENCE_STICKY_TOP;
  const sequenceTopHeaderScreenY = viewport.y + SEQUENCE_HEADER_Y * viewport.scale;
  const sequenceBottomHeaderScreenY = viewport.y + sequenceLifelineBottom * viewport.scale;
  const stickyModeRef = useRef({ topPinned: false, bottomPinned: false, stickyScale: 1 });
  const stickyMode = useMemo(() => {
    stickyModeRef.current = resolveStickyMode(stickyModeRef.current, {
      diagramType,
      topHeaderScreenY: sequenceTopHeaderScreenY,
      bottomHeaderScreenY: sequenceBottomHeaderScreenY,
      viewportHeight: windowSize.height,
      stickyTopOffset,
      viewportScale: viewport.scale,
      stickyBottomInset: SEQUENCE_STICKY_BOTTOM_INSET,
      headerHeight: SEQUENCE_HEADER_HEIGHT,
    });
    return stickyModeRef.current;
  }, [diagramType, sequenceTopHeaderScreenY, sequenceBottomHeaderScreenY, windowSize.height, stickyTopOffset, viewport.scale]);
  const stickyHeaderScreenHeight = computeStickyHeaderScreenHeight(viewport.scale, stickyMode.stickyScale, SEQUENCE_HEADER_HEIGHT);
  const stickyBottomHeaderScreenY = windowSize.height - SEQUENCE_STICKY_BOTTOM_INSET - stickyHeaderScreenHeight;
  const stickyTopHeaderCanvasY = stickyMode.topPinned
    ? screenToCanvasY(stickyTopOffset, viewport.y, viewport.scale)
    : SEQUENCE_HEADER_Y;
  const stickyBottomHeaderCanvasY = stickyMode.bottomPinned
    ? screenToCanvasY(stickyBottomHeaderScreenY, viewport.y, viewport.scale)
    : sequenceLifelineBottom;
  const stickyMaskBands = useMemo(() => (
    computeStickyMaskBandScreenRects({
      state: stickyMode,
      stickyTopOffset,
      viewportHeight: windowSize.height,
      stickyBottomInset: SEQUENCE_STICKY_BOTTOM_INSET,
      headerScreenHeight: stickyHeaderScreenHeight,
    })
  ), [stickyMode, stickyTopOffset, windowSize.height, stickyHeaderScreenHeight]);

  // ── Undo/Redo helpers ────────────────────────────────────────────────────
  const { canUndo, canRedo } = useUndoStore();
  // snapshotFromStore reads all fields directly from the store — layers and flows are always included.
  const snapshotBeforeMutation = snapshotFromStore;

  const showToast = useCallback((message: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, id: Date.now() });
    toastTimerRef.current = setTimeout(() => setToast(null), 2500);
  }, []);

  const {
    exportAllPagesAsPng,
    setExportAllPagesAsPng,
    exportPngMonochrome,
    setExportPngMonochrome,
    isPngExporting,
    handleExportImage,
    handleConfirmPngExport,
  } = useDiagramExport({
    stageRef,
    sequenceLifelineBottom,
    projectFileStem,
    activeProjectName,
    showToast,
  });
  const handlePublishLink = usePublishLink(activeProjectName, showToast);

  // ── Page navigation with history (Option 3: deduplication) ──────────────
  const {
    pageHistory,
    historyIndex,
    switchPageWithHistory,
    goBack,
    goForward,
    goHome,
    handleDeletePage,
  } = usePageNavigation({
    pages,
    activePage,
    switchPage,
    deletePage: _deletePage,
    canMutateStructure: interactionPolicy.canMutateStructure,
    showToast,
  });

  const {
    altDividerDragBaselineRef,
    fragmentResizeBaselineRef,
    messageDragBaselineRef,
    activeAltDividerDragRef,
    activeFragmentResizeRef,
    sequencePerfHistory,
    beginSequenceDragPerf,
    recordSequenceDragPerf,
    endSequenceDragPerf,
    captureSequenceFragmentDragBaseline,
    restoreSequenceFragmentDragBaseline,
    applyAltDividerDrag,
    applyFragmentEndDrag,
    ensureFragmentCascadeEndY,
    scheduleAltDividerDragMove,
    scheduleFragmentResizeMove,
    cancelScheduledAltDividerDragMove,
    cancelScheduledFragmentResizeMove,
  } = useSequenceDrag({ applySequenceLayoutPatch });

  const {
    getSequenceMessageFootprint,
    applyMessageDragWithPush,
    applyMessageDragWithReorder,
    resolveDraggedMessageIds,
    ensureDraggedMessageFragmentExtents,
    finalizeDraggedMessages,
    addSequenceMessageWithLayout,
    enforceSequenceMessageSpacing,
  } = useSequenceMessageLayout({
    participants,
    messages,
    fragments,
    selectedItems,
    applySequenceLayoutPatch,
    addMessage,
    updateFragment,
    snapshotBeforeMutation,
    canMutateStructure: interactionPolicy.canMutateStructure,
    ensureFragmentCascadeEndY,
    dragSelectedMessageIdsRef,
    dragReorderSnapshotRef,
    reorderMessageDragKeyRef,
  });

  const clearHoveredSidebarTargets = useCallback((except?: 'node' | 'participant' | 'message') => {
    if (except !== 'node') setHoveredNodeId(null);
    if (except !== 'participant') setHoveredParticipantId(null);
    if (except !== 'message') setHoveredMessageId(null);
  }, []);

  const handleSidebarHoverStart = useCallback((kind: 'node' | 'participant' | 'message', id: string) => {
    clearHoveredSidebarTargets(kind);
    if (kind === 'node') setHoveredNodeId(id);
    if (kind === 'participant') setHoveredParticipantId(id);
    if (kind === 'message') setHoveredMessageId(id);
  }, [clearHoveredSidebarTargets]);

  const handleSidebarHoverEnd = useCallback((kind: 'node' | 'participant' | 'message') => {
    if (kind === 'node') setHoveredNodeId(null);
    if (kind === 'participant') setHoveredParticipantId(null);
    if (kind === 'message') setHoveredMessageId(null);
  }, []);

  const resetSidebarHoverState = useCallback(() => {
    clearHoveredSidebarTargets();
  }, [clearHoveredSidebarTargets]);

  useEffect(() => {
    const handleWindowPointerMove = (event: PointerEvent) => {
      if (!hoveredNodeId && !hoveredParticipantId && !hoveredMessageId) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-canvas-area]')) return;
      clearHoveredSidebarTargets();
      setSeqMsgTooltip(null);
    };

    window.addEventListener('pointermove', handleWindowPointerMove, true);
    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove, true);
    };
  }, [
    hoveredNodeId,
    hoveredParticipantId,
    hoveredMessageId,
    clearHoveredSidebarTargets,
  ]);

  const getNextProjectName = useCallback((projectDiagramType: 'flow' | 'sequence' | 'c4') => {
    const baseName = projectDiagramType === 'sequence'
      ? 'Sequence Project'
      : projectDiagramType === 'c4'
        ? 'C4 Workspace'
        : 'Flow Project';
    if (!projects.some(project => project.name === baseName)) return baseName;
    let index = 2;
    while (projects.some(project => project.name === `${baseName} ${index}`)) index += 1;
    return `${baseName} ${index}`;
  }, [projects]);

  const handleCreateProject = useCallback((projectDiagramType: 'flow' | 'sequence' | 'c4') => {
    if (!interactionPolicy.canMutateStructure) return;
    if (projectDiagramType === 'c4') {
      createProject(getNextProjectName('c4'), 'flow');
      useDiagramStore.getState().createC4Workspace('System');
      revealHeader();
      setOpenDropdown(null);
      return;
    }
    createProject(getNextProjectName(projectDiagramType), projectDiagramType);
    revealHeader();
    setOpenDropdown(null);
  }, [createProject, getNextProjectName, interactionPolicy.canMutateStructure, revealHeader]);

  const handleStartC4Workspace = useCallback((systemName?: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    const st = useDiagramStore.getState();
    const hasContent =
      st.nodes.length > 0 ||
      st.edges.length > 0 ||
      st.groups.length > 0 ||
      st.participants.length > 0 ||
      st.pages.some(page =>
        page.nodes.length > 0 ||
        (page.participants?.length ?? 0) > 0 ||
        (page.messages?.length ?? 0) > 0,
      );
    if (hasContent && !window.confirm('Replace the current diagram with a C4 workspace?')) return;
    snapshotFromStore();
    st.createC4Workspace(systemName ?? 'System');
    setEmptyStateDismissed(false);
    revealHeader();
    setOpenDropdown(null);
  }, [interactionPolicy.canMutateStructure, revealHeader]);

  useEffect(() => {
    setEmptyStateDismissed(false);
  }, [activePage]);

  useEffect(() => {
    if (!activeProjectId) return;
    syncHeaderForProject(activeProjectId);
  }, [activeProjectId, syncHeaderForProject]);

  // Auto-center only when a sequence page is still at its untouched default viewport.
  // This preserves a user's saved/manual camera on reload and during later edits.
  useEffect(() => {
    if (diagramType !== 'sequence' || participants.length === 0) return;
    const viewportIsDefault =
      Math.abs(viewport.x) < 0.5
      && Math.abs(viewport.y) < 0.5
      && Math.abs(viewport.scale - 1) < 0.001;
    if (!viewportIsDefault) return;

    const bounds = computeDiagramContentBounds({
      nodes: useDiagramStore.getState().nodes,
      edges: useDiagramStore.getState().edges,
      groups: useDiagramStore.getState().groups,
      participants: useDiagramStore.getState().participants,
      messages: useDiagramStore.getState().messages,
      fragments: useDiagramStore.getState().fragments,
      seqLifelineLength: useDiagramStore.getState().seqLifelineLength,
    });
    const minScale = computeDynamicMinScale(
      bounds,
      windowSize.width,
      windowSize.height,
      CANVAS.SCALE_MIN,
    );

    const HEADER_WIDTH = 140;
    const HEADER_HEIGHT = 44;
    const HEADER_Y = 40;
    const minX = Math.min(...participants.map(p => p.x));
    const maxX = Math.max(...participants.map(p => p.x + HEADER_WIDTH));
    const contentW = maxX - minX;
    const contentH = seqLifelineLength + HEADER_HEIGHT + 80;
    const padding = 80;
    const scaleX = (windowSize.width - padding * 2) / contentW;
    const scaleY = (windowSize.height - padding * 2) / contentH;
    const scale = Math.min(CANVAS.SCALE_MAX, Math.max(minScale, Math.min(scaleX, scaleY, 1.2)));
    const vx = (windowSize.width - contentW * scale) / 2 - minX * scale;
    const vy = (windowSize.height - contentH * scale) / 2 - HEADER_Y * scale;
    updateViewport({ x: vx, y: vy, scale });
  }, [
    activePage,
    diagramType,
    participants,
    seqLifelineLength,
    updateViewport,
    viewport.scale,
    viewport.x,
    viewport.y,
    windowSize.height,
    windowSize.width,
  ]);

  // ── Keyboard shortcuts for page navigation ──────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
      const isCtrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
      
      // Cmd/Ctrl + [ for back
      if (isCtrlOrCmd && e.key === '[') {
        e.preventDefault();
        usedViaKeyboard('page-back');
        goBack();
      }
      // Cmd/Ctrl + ] for forward
      if (isCtrlOrCmd && e.key === ']') {
        e.preventDefault();
        usedViaKeyboard('page-forward');
        goForward();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goBack, goForward]);

  // ── Track mouse position for smart node placement ──────────────────────
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  // ── Resize ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onHashChange = () => setLocationHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    setDiagramPersistenceEnabled(interactionPolicy.canPersistChanges);
    return () => {
      setDiagramPersistenceEnabled(true);
    };
  }, [interactionPolicy.canPersistChanges]);

  // ── Auto-edit after node add ─────────────────────────────────────────────
  useEffect(() => {
    if (pendingEditAfterAdd.current && nodes.length > prevNodesLen.current) {
      const newNode = nodes[nodes.length - 1];
      if (newNode) setInlineEdit({ nodeId: newNode.id, field: 'label' });
      pendingEditAfterAdd.current = false;
    }
    prevNodesLen.current = nodes.length;
  }, [nodes]);

  // ── Layer auto-rename after add ──────────────────────────────────────────
  useEffect(() => {
    if (pendingLayerEdit.current && layers.length > prevLayersLen.current) {
      setEditingLayerId(layers[layers.length - 1].id);
      pendingLayerEdit.current = false;
    }
    prevLayersLen.current = layers.length;
  }, [layers]);

  // ── Auto-edit after message add ─────────────────────────────────────────
  useEffect(() => {
    if (pendingEditY !== null && messages.length > 0) {
      // Find the message closest to the Y where the drag ended (that's the newly added one)
      const closest = messages.reduce((best, m) =>
        Math.abs(m.y - pendingEditY) < Math.abs(best.y - pendingEditY) ? m : best
      );
      setInlineEdgeEdit({ edgeId: closest.id });
      setPendingEditY(null);
    }
  }, [messages, pendingEditY]);

  // ── Project store init ───────────────────────────────────────────────────
  const hasBootstrapped = useRef(false);
  useEffect(() => {
    if (isViewOnly) return;
    loadProjects();
    // After storage has loaded (synchronous localStorage read), check if we need to bootstrap.
    // We defer by one microtask to let Zustand setState propagate before reading.
    setTimeout(() => {
      if (hasBootstrapped.current) return;
      hasBootstrapped.current = true;
      const { projects } = useProjectStore.getState();
      if (projects.length === 0) {
        // No projects in storage at all — first ever run. Create a default one silently.
        useProjectStore.getState().createProject('My Project');
      }
    }, 0);
  }, [isViewOnly, loadProjects]);
  // Auto-save active project whenever diagram changes (debounced)
  useEffect(() => {
    if (isViewOnly) return;
    if (!activeProjectId) return;
    const t = setTimeout(() => saveActiveProject(), 1500);
    return () => clearTimeout(t);
  }, [nodes, edges, groups, layers, viewport, pages, participants, messages, fragments, catalog, activeProjectId, isViewOnly, saveActiveProject]);

  useEffect(() => {
    if (!publishedFromHash) return;
    try {
      loadDiagram(loadFromJSON(publishedFromHash.json));
      setSidebarTab('pages');
      setIsToolboxOpen(true);
      revealHeader();
      setToast({ message: 'Opened published diagram in view-only mode', id: Date.now() });
    } catch (error) {
      setToast({ message: 'Failed to load published diagram URL', id: Date.now() });
    }
  }, [publishedFromHash, loadDiagram, revealHeader]);

  useEffect(() => {
    if (showLocalDevTools) return;
    if (isJsonPanelOpen) setIsJsonPanelOpen(false);
    if (isDevStateViewerOpen) setIsDevStateViewerOpen(false);
  }, [isDevStateViewerOpen, isJsonPanelOpen, showLocalDevTools]);

  // ── JSON panel auto-sync ─────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setJsonText(saveToJSON({ diagramType, participants, messages, fragments, participantGroups, nodes, edges, viewport, layers, groups, graphs, pages, activePage, catalog })), 400);
    return () => clearTimeout(t);
  }, [diagramType, participants, messages, fragments, participantGroups, nodes, edges, viewport, layers, groups, graphs, pages, activePage, catalog]);

  // ── Auto-select text in inline edit fields ──────────────────────────
  useEffect(() => {
    if (!inlineEdit) return;
    const timer = setTimeout(() => {
      const editNode = useDiagramStore.getState().nodes.find(n => n.id === inlineEdit.nodeId);
      const useArea = inlineEdit.field === 'description'
        || editNode?.type === 'text'
        || editNode?.type === 'note'
        || (editNode ? usesWrappedCenteredLabel(editNode.type) : false);
      if (useArea && inlineEditTextareaRef.current) {
        inlineEditTextareaRef.current.select();
      } else if (inlineEdit.field === 'label' && inlineEditInputRef.current) {
        inlineEditInputRef.current.select();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [inlineEdit]);

  // ── Quick node creation menu outside click ─────────────────────────────────
  const quickNodeMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!quickNodeCreation?.show) return;
    const handler = (e: MouseEvent) => {
      if (quickNodeMenuRef.current?.contains(e.target as Node)) return;
      useDiagramStore.getState().cancelConnection();
      setQuickNodeCreation(null);
    };
    // Delay adding the listener to avoid immediate trigger from the same click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [quickNodeCreation?.show]);

  // ── Coordinate helpers ───────────────────────────────────────────────────
  const toCanvas = useCallback((sx: number, sy: number) => ({
    x: (sx - viewport.x) / viewport.scale,
    y: (sy - viewport.y) / viewport.scale,
  }), [viewport.x, viewport.y, viewport.scale]);

  // ── Keyboard ─────────────────────────────────────────────────────────────
  useAppKeyboard({
    setSpaceHeld, setChordHint, setIsFrameDrawMode, setCanvasDrawTool, setMarkupStroke, setColorPicker,
    setIsMermaidModalOpen, setIsDevStateViewerOpen, setIsSettingsOpen,
    setInlineEdit, setInlineSocketEdit, setTooltip, setContextMenu,
    setInfoPanelNodeId, setInfoPanelParticipantId, setInfoPanelMessageId,
    setSequenceDrag, setActiveFlowId, setQuickNodeCreation,
    setFrameDrawRect, setAnchorNodeId, setDiscoveryMode, setSeqClipboard,
    setIsCut, setSidebarTab, setIsToolboxOpen, showToast,
    setIsDistractionFree, setIsChordModalOpen, isDistractionFree,
    chordBuffer, chordTimer, rKeyHeld, bKeyHeld, mKeyHeld, eKeyHeld, frameDrawStart,
    discoveryTimerRef, lastMousePos, lastNodeAddPos, lastChordAddCanvasPos,
    lastAddedNodeId, pendingEditAfterAdd, cursorCanvasPos,
    contextMenu, infoPanelNodeId, infoPanelParticipantId, infoPanelMessageId,
    inlineEdit, tooltip, connectionState,
    selectedItems, isSettingsOpen, isMermaidModalOpen, isDevStateViewerOpen,
    isFrameDrawMode, canvasDrawTool, canvasStampType, setCanvasStampType, colorPicker, inlineSocketEdit, sequenceDrag,
    activeFlowId, quickNodeCreation, viewport, windowSize,
    diagramType, seqClipboard, isCut, isReadOnly: isViewOnly, toCanvas,
  });

  // ── Add node (per-type counter) ──────────────────────────────────────────
  const syncStackMembership = useCallback((nodeId: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    const st = useDiagramStore.getState();
    const latest = st.nodes;
    const node = latest.find(n => n.id === nodeId);
    if (!node || !isStackableDrawing(node.type)) return;
    const parent = findStackParent(node, latest);
    const nextId = parent?.id;
    const current = getStackParentId(node);
    const heldSnap = getStackSnap(node);
    const snap = parent ? detectStackSnap(node, parent, heldSnap) : undefined;
    const nextPos = parent && snap ? snappedPosition(node, parent, snap) : node.position;
    const nextData = { ...node.data };
    if (nextId) nextData.stackParentId = nextId;
    else delete nextData.stackParentId;
    if (snap) nextData.stackSnap = snap;
    else delete nextData.stackSnap;
    const dx = nextPos.x - node.position.x;
    const dy = nextPos.y - node.position.y;
    if (nextId !== current || snap !== heldSnap || dx !== 0 || dy !== 0) {
      updateNode(nodeId, { position: nextPos, data: nextData });
      if (dx !== 0 || dy !== 0) {
        for (const childId of collectStackDescendantIds(nodeId, latest)) {
          const child = useDiagramStore.getState().nodes.find(n => n.id === childId);
          if (!child) continue;
          updateNode(childId, { position: { x: child.position.x + dx, y: child.position.y + dy } });
        }
      }
    }
    if (nextId && nextId !== current) {
      st.moveStackAboveParent(nodeId, nextId);
    }
  }, [interactionPolicy.canMutateStructure, updateNode]);

  const handleAddNode = useCallback((type: string, screenPos?: { x: number; y: number }) => {
    if (!interactionPolicy.canMutateStructure) return;
    const isGroup = type === 'group';
    const st = useDiagramStore.getState();
    const sameTypeCount = st.nodes.filter(n => n.type === type).length;
    const blueprint = NodeFactory.create(type, sameTypeCount);

    // Determine node position: use screenPos if provided, otherwise check if mouse moved significantly
    let finalPos = screenPos;
    if (!screenPos && lastMousePos.current && lastNodeAddPos.current) {
      const dx = lastMousePos.current.x - lastNodeAddPos.current.x;
      const dy = lastMousePos.current.y - lastNodeAddPos.current.y;
      if (dx * dx + dy * dy > 6400 && lastMousePos.current.x > 400) {
        finalPos = lastMousePos.current;
      }
    }

    const position = finalPos
      ? toCanvas(finalPos.x, finalPos.y)
      : toCanvas(
        Math.random() * (windowSize.width - 300) + 100,
        Math.random() * (windowSize.height - 200) + 80,
      );

    const currentGroupId = groupNavStack.length > 0 ? groupNavStack[groupNavStack.length - 1].groupId : undefined;
    if (!blueprint.skipAutoEdit) pendingEditAfterAdd.current = true;
    snapshotBeforeMutation();
    usedViaPointerNodeType(type);

    // Special handling for 'group' type: create an empty group with a child graph
    if (isGroup) {
      const groupNodeId = st.createEmptyGroup(position, blueprint.label);
      lastNodeAddPos.current = finalPos || { x: Math.random() * (windowSize.width - 300) + 100, y: Math.random() * (windowSize.height - 200) + 80 };
      lastAddedNodeId.current = groupNodeId;
      return;
    }

    addNode({ type, position, size: blueprint.size, label: blueprint.label, sockets: blueprint.sockets, data: withStickyDrawColor(blueprint.data), parentGroupId: currentGroupId });
    lastNodeAddPos.current = finalPos || { x: Math.random() * (windowSize.width - 300) + 100, y: Math.random() * (windowSize.height - 200) + 80 };
    const allNodes = useDiagramStore.getState().nodes;
    const newNode = allNodes[allNodes.length - 1];
    lastAddedNodeId.current = newNode?.id ?? null;
    if (newNode) setAnchorNodeId(newNode.id);
    if (currentGroupId && newNode) addNodeToGroup(newNode.id, currentGroupId);
    if (newNode) syncStackMembership(newNode.id);
  }, [toCanvas, addNode, addNodeToGroup, groupNavStack, windowSize, snapshotBeforeMutation, interactionPolicy.canMutateStructure, syncStackMembership]);

  const handleDragFromPanel = useCallback((nodeType: string, pos: { x: number; y: number }) => {
    handleAddNode(nodeType, pos);
  }, [handleAddNode]);

  // Auto-capture: check which frame a collapsed group belongs to based on its center position
  const checkGroupFrameMembership = useCallback((groupId: string, pos: { x: number; y: number }, sz: { width: number; height: number }) => {
    const cx = pos.x + sz.width / 2;
    const cy = pos.y + sz.height / 2;
    const frames = groups.filter(g => g.isFrame && g.id !== groupId);
    const containing = frames
      .filter(f => cx > f.position.x && cx < f.position.x + f.size.width &&
                   cy > f.position.y && cy < f.position.y + f.size.height)
      .sort((a, b) => (a.size.width * a.size.height) - (b.size.width * b.size.height));
    const targetFrame = containing[0] ?? null;
    const grp = groups.find(g => g.id === groupId);
    if (!grp) return;
    const currentFrameId = grp.parentGroupId && groups.find(g => g.id === grp.parentGroupId)?.isFrame
      ? grp.parentGroupId : null;
    if (targetFrame?.id !== currentFrameId) {
      if (currentFrameId) removeGroupFromFrame(groupId, currentFrameId);
      if (targetFrame) addGroupToFrame(groupId, targetFrame.id);
    }
  }, [groups, addGroupToFrame, removeGroupFromFrame]);

  // Auto-capture: check which frame a node belongs to based on its center position
  const checkFrameMembership = useCallback((nodeId: string, pos: { x: number; y: number }, sz: { width: number; height: number }) => {
    if (!interactionPolicy.canMutateStructure) return;
    const cx = pos.x + sz.width / 2;
    const cy = pos.y + sz.height / 2;
    const frames = groups.filter(g => g.isFrame);
    const containing = frames
      .filter(f => cx > f.position.x && cx < f.position.x + f.size.width &&
                   cy > f.position.y && cy < f.position.y + f.size.height)
      .sort((a, b) => (a.size.width * a.size.height) - (b.size.width * b.size.height));
    const targetFrame = containing[0] ?? null;
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    const currentFrameId = node.parentGroupId && groups.find(g => g.id === node.parentGroupId)?.isFrame
      ? node.parentGroupId : null;
    if (targetFrame?.id !== currentFrameId) {
      if (currentFrameId) removeNodeFromGroup(nodeId, currentFrameId);
      if (targetFrame) addNodeToGroup(nodeId, targetFrame.id);
    }
  }, [groups, nodes, addNodeToGroup, removeNodeFromGroup, interactionPolicy.canMutateStructure]);

  // ── Multi-select drag ─────────────────────────────────────────────────────
  const { dragOverridePos, handleMultiDragStart, handleMultiDragMove, handleMultiDragEnd } =
    useMultiDrag(selectedItems, nodes, updateNode, checkFrameMembership, syncStackMembership);


  // ── Beautify Layout (Sugiyama hierarchical) ──────────────────────────────
  const handleBeautifyLayout = useCallback(async () => {
    if (!interactionPolicy.canMutateStructure) return;
    if (nodes.length === 0) return;
    // The author's own choice of direction, falling back to whatever the
    // active kit reads best as.
    const rankAxis = resolveRankAxis(layoutDirection, activeKit.layout);
    // The layout engine is fetched on first use and runs off the main pass, so
    // nothing is written to the store until it comes back with an answer - a
    // failed run leaves the diagram exactly as the author left it.
    try {
      const layout = await LayoutService.layout(nodes, edges, groups, viewport, windowSize, { rankAxis });
      snapshotBeforeMutation();
      const st = useDiagramStore.getState();
      st.applyLayout(layout);
      st.groups.forEach(group => {
        if (!layout.frames[group.id]) st.recalcGroupBounds(group.id);
      });
    } catch (error) {
      console.error('Beautify layout failed', error);
    }
  }, [nodes, edges, groups, viewport, windowSize, snapshotBeforeMutation, interactionPolicy.canMutateStructure, activeKit.layout, layoutDirection]);

  // ── Connection ───────────────────────────────────────────────────────────
  const handleSocketMouseDown = useCallback((nodeId: string, socketId: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    // M held + socket has existing edges → move those edges (detach & reconnect)
    if (mKeyHeld.current) {
      const st = useDiagramStore.getState();
      const edgesFromSocket = st.edges.filter(e =>
        (e.sourceNodeId === nodeId && e.sourceSocketId === socketId) ||
        (e.targetNodeId === nodeId && e.targetSocketId === socketId)
      );
      if (edgesFromSocket.length > 0) {
        // Pick first edge to move; store it so mouseUp can retarget it
        const edgeToMove = edgesFromSocket[0];
        // Determine which end we're dragging from
        const movingSource = edgeToMove.sourceNodeId === nodeId && edgeToMove.sourceSocketId === socketId;
        // Start a normal connection drag from the OTHER end of the edge
        const anchorNodeId = movingSource ? edgeToMove.targetNodeId : edgeToMove.sourceNodeId;
        const anchorSocketId = movingSource ? edgeToMove.targetSocketId : edgeToMove.sourceSocketId;
        // Store the edge id to retarget on mouseUp
        pendingEdgeMoveRef.current = { edgeId: edgeToMove.id, anchorNodeId, anchorSocketId, movingSource };
        snapshotBeforeMutation();
        startConnection(anchorNodeId, anchorSocketId);
        return;
      }
    }
    startConnection(nodeId, socketId);
  }, [startConnection, snapshotBeforeMutation, interactionPolicy.canMutateStructure]);

  const handleSocketMouseUp = useCallback((nodeId: string, socketId: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    if (!connectionState.isConnecting) return;

    // M-mode: retarget the existing edge instead of creating a new one
    if (pendingEdgeMoveRef.current) {
      const { edgeId, movingSource } = pendingEdgeMoveRef.current;
      pendingEdgeMoveRef.current = null;
      const st = useDiagramStore.getState();
      // Cancel the temp connection state
      st.cancelConnection();
      // Retarget the edge to the new socket
      if (movingSource) {
        updateEdge(edgeId, { sourceNodeId: nodeId, sourceSocketId: socketId });
      } else {
        updateEdge(edgeId, { targetNodeId: nodeId, targetSocketId: socketId });
      }
      return;
    }

    snapshotBeforeMutation();
    const newEdge = completeConnection(nodeId, socketId);
    if (newEdge) {
      setInlineEdgeEdit({ edgeId: newEdge.id });
    }
  }, [connectionState.isConnecting, completeConnection, snapshotBeforeMutation, updateEdge, interactionPolicy.canMutateStructure]);

  // Derive source socket type for ghost socket eligibility and direction
  const connectionSourceSocketType = useMemo((): 'input' | 'output' | null => {
    if (!connectionState.isConnecting || !connectionState.sourceNodeId || !connectionState.sourceSocketId) return null;
    const srcNode = nodes.find(n => n.id === connectionState.sourceNodeId);
    if (!srcNode) return null;
    if (isMagneticNode(srcNode.type)) return 'output';
    return srcNode.sockets.find(s => s.id === connectionState.sourceSocketId)?.type ?? null;
  }, [connectionState.isConnecting, connectionState.sourceNodeId, connectionState.sourceSocketId, nodes]);

  const handleGhostSocketDrop = useCallback((nodeId: string, ghostType: 'input' | 'output') => {
    if (!interactionPolicy.canMutateStructure) return;
    if (!connectionState.isConnecting) return;
    const { sourceNodeId, sourceSocketId } = connectionState;
    if (!sourceNodeId || !sourceSocketId) return;

    snapshotBeforeMutation();
    const newSocketId = addSocket(nodeId, ghostType);
    if (!newSocketId) return;

    if (connectionSourceSocketType === 'output') {
      // Source is output, new socket is input — standard direction
      const newEdge = completeConnection(nodeId, newSocketId);
      if (newEdge) setInlineEdgeEdit({ edgeId: newEdge.id });
    } else {
      // Source is input, new socket is output — swap so edge goes output → input
      const st = useDiagramStore.getState();
      st.cancelConnection();
      st.startConnection(nodeId, newSocketId);
      const newEdge = st.completeConnection(sourceNodeId, sourceSocketId);
      if (newEdge) setInlineEdgeEdit({ edgeId: newEdge.id });
    }
  }, [
    interactionPolicy.canMutateStructure, connectionState, connectionSourceSocketType,
    snapshotBeforeMutation, addSocket, completeConnection, setInlineEdgeEdit,
  ]);

  const handleMagneticConnectStart = useCallback((nodeId: string, localX: number, localY: number): boolean => {
    if (!interactionPolicy.canMutateStructure) return false;
    const node = useDiagramStore.getState().nodes.find(n => n.id === nodeId);
    if (!node) return false;
    const socket = nearestSocket(
      node,
      node.position.x + localX,
      node.position.y + localY,
      node.sockets,
      MAGNETIC_SOCKET_SNAP_PX,
    );
    if (!socket) return false;
    startConnection(nodeId, socket.id);
    return true;
  }, [interactionPolicy.canMutateStructure, startConnection]);

  const handleMagneticConnectDrop = useCallback((nodeId: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    const st = useDiagramStore.getState();
    if (!st.connectionState.isConnecting) return;
    if (nodeId === st.connectionState.sourceNodeId) return;
    const source = st.nodes.find(n => n.id === st.connectionState.sourceNodeId);
    const target = st.nodes.find(n => n.id === nodeId);
    const sourceSocket = source?.sockets.find(s => s.id === st.connectionState.sourceSocketId);
    if (!source || !target || !sourceSocket) return;
    const targetSocket = pickMagneticTargetSocket(source, sourceSocket, target);
    if (!targetSocket) return;
    handleSocketMouseUp(nodeId, targetSocket.id);
  }, [handleSocketMouseUp, interactionPolicy.canMutateStructure]);

  const handleSocketSlideStart = useCallback((nodeId: string, socketId: string) => {
    if (!interactionPolicy.canMutateStyles) return;
    slidingSocketRef.current = { nodeId, socketId };
  }, [interactionPolicy.canMutateStyles]);

  const handleSocketDoubleClick = useCallback((nodeId: string, socketId: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    if (diagramType !== 'flow' || activeFlowId) return;
    setInlineSocketEdit({ nodeId, socketId });
  }, [diagramType, activeFlowId, interactionPolicy.canMutateStructure]);

  const handleLifelineMouseDown = useCallback((participantId: string, y: number) => {
    if (!interactionPolicy.canMutateStructure) return;
    const p = participants.find(pp => pp.id === participantId);
    if (!p) return;
    setSequenceDrag({
      sourceParticipantId: participantId,
      sourceX: p.x + 70, // center of 140px header
      startY: y,
    });
  }, [participants, interactionPolicy.canMutateStructure]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLifelineMouseUp = useCallback((participantId: string, _y: number) => {
    if (!interactionPolicy.canMutateStructure) return;
    if (!sequenceDrag) return;
    const msgY = addSequenceMessageWithLayout({
      sourceId: sequenceDrag.sourceParticipantId,
      targetId: participantId,
      proposedY: sequenceDrag.startY,
      label: '',
      type: 'sync',
    });
    setSequenceDrag(null);
    setPendingEditY(msgY);
  }, [sequenceDrag, addSequenceMessageWithLayout, interactionPolicy.canMutateStructure]);
  const placeMarkupNode = useCallback((type: 'text' | 'note' | 'arrow' | 'line', position: Point, end?: Point) => {
    if (!interactionPolicy.canMutateStructure) return;
    const st = useDiagramStore.getState();
    const blueprint = NodeFactory.create(type, st.nodes.filter(n => n.type === type).length);
    let pos = position;
    let size = blueprint.size;
    let data = { ...blueprint.data };
    if (end && isStrokeMarkup(type)) {
      const geom = arrowFromPoints(position, end);
      pos = geom.position;
      size = geom.size;
      data = { ...data, from: geom.from, to: geom.to };
    } else if (type === 'text') {
      size = fitTextNodeSize(blueprint.label);
    }
    snapshotBeforeMutation();
    const before = new Set(st.nodes.map(n => n.id));
    const currentGroupId = st.groupNavStack.length > 0 ? st.groupNavStack[st.groupNavStack.length - 1].groupId : undefined;
    addNode({
      type,
      label: blueprint.label,
      position: pos,
      size,
      sockets: [],
      data: withStickyDrawColor(data),
      parentGroupId: currentGroupId,
    });
    const created = useDiagramStore.getState().nodes.find(n => !before.has(n.id));
    if (created) {
      selectItem(created.id);
      lastAddedNodeId.current = created.id;
      if (currentGroupId) addNodeToGroup(created.id, currentGroupId);
      if (!blueprint.skipAutoEdit) setInlineEdit({ nodeId: created.id, field: 'label' });
      syncStackMembership(created.id);
    }
  }, [addNode, addNodeToGroup, interactionPolicy.canMutateStructure, selectItem, snapshotBeforeMutation, syncStackMembership]);

  const handleStampDrawn = useCallback((type: string, rect: { x: number; y: number; w: number; h: number }) => {
    if (!interactionPolicy.canMutateStructure) return;
    const st = useDiagramStore.getState();
    const count = st.nodes.filter(n => n.type === type).length;
    const blueprint = NodeFactory.create(type, count);
    const snap = (n: number) => Math.round(n / 8) * 8;
    const defaultW = blueprint.size.width;
    const defaultH = blueprint.size.height;
    const { width: minW, height: minH } = magneticMinSize(type);
    let width = rect.w < 12 ? defaultW : Math.max(minW, snap(rect.w));
    let height = rect.h < 12 ? defaultH : Math.max(minH, snap(rect.h));
    if (locksEqualAspect(type) && rect.w >= 12) {
      const side = Math.max(width, height);
      width = side;
      height = side;
    }
    const x = rect.w < 12 ? snap(rect.x - width / 2) : snap(rect.x);
    const y = rect.h < 12 ? snap(rect.y - height / 2) : snap(rect.y);
    const sockets = isMagneticNode(type)
      ? rebuildSketchSockets(type, width, height, 0)
      : blueprint.sockets.map(s => s.type === 'output' ? { ...s, position: { ...s.position, x: width } } : s);
    const currentGroupId = st.groupNavStack.length > 0 ? st.groupNavStack[st.groupNavStack.length - 1].groupId : undefined;
    snapshotBeforeMutation();
    addNode({
      type,
      position: { x, y },
      size: { width, height },
      label: blueprint.label,
      sockets,
      data: withStickyDrawColor(blueprint.data),
      parentGroupId: currentGroupId,
    });
    const created = useDiagramStore.getState().nodes[useDiagramStore.getState().nodes.length - 1];
    if (created) {
      selectItem(created.id);
      lastAddedNodeId.current = created.id;
      setAnchorNodeId(created.id);
      if (currentGroupId) addNodeToGroup(created.id, currentGroupId);
      syncStackMembership(created.id);
    }
  }, [addNode, addNodeToGroup, interactionPolicy.canMutateStructure, selectItem, snapshotBeforeMutation, syncStackMembership]);

  // ── Pan / zoom / marquee / frame-draw ────────────────────────────────────
  const { handleWheel, handleStageMouseDown, handleCanvasMouseMove, handleCanvasMouseUp, handleCanvasClick } =
    useCanvasHandlers({
      viewport, minScale: dynamicMinScale, spaceHeld, isFrameDrawMode, canvasDrawTool, canvasStampType, isPanning, activeFlowId, isReadOnly: isViewOnly,
      connectionState, diagramType, nodes, edges, groups, participants, messages, fragments,
      marqueeRect, lassoPoints, frameDrawRect, markupStroke, sequenceDrag,
      setIsPanning, setMarqueeRect, setLassoPoints, setFrameDrawRect, setMarkupStroke, setIsFrameDrawMode, setCanvasDrawTool,
      setSequenceDrag, setQuickNodeCreation, setPendingEditY, setAnchorNodeId,
      panStart, leftPanPending, canvasMouseDownPos, marqueeStart, frameDrawStart, bKeyHeld, cursorCanvasPos,
      updateViewport, updateTempEdge, selectItem, clearSelection, createFrame, addMessage,
      onFrameCreated: (frameId: string) => setInlineGroupEdit({ groupId: frameId }),
      onMarkupStrokeComplete: (kind, start, end) => placeMarkupNode(kind, start, end),
      onMarkupTextPlace: (position) => placeMarkupNode('text', position),
      onStampDrawn: handleStampDrawn,
      toCanvas,
    });

  // Handle quick node creation during edge drag
  const handleQuickNodeCreation = useCallback((nodeType: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    if (!quickNodeCreation?.show || !connectionState.isConnecting || !connectionState.tempEdge) return;

    usedViaPointerNodeType(nodeType);
    const st = useDiagramStore.getState();
    const srcNode = st.nodes.find(n => n.id === connectionState.sourceNodeId);
    if (!srcNode) return;

    snapshotBeforeMutation();

    const sameTypeCount = st.nodes.filter(n => n.type === nodeType).length;
    const blueprint = NodeFactory.create(nodeType, sameTypeCount);
    const activeGrpId = st.groupNavStack.length > 0 ? st.groupNavStack[st.groupNavStack.length - 1].groupId : undefined;
    const canvasPos = toCanvas(quickNodeCreation.screenX, quickNodeCreation.screenY);

    st.addNode({
      type: nodeType,
      position: canvasPos,
      size: blueprint.size,
      label: blueprint.label,
      sockets: blueprint.sockets,
      data: withStickyDrawColor(blueprint.data),
      parentGroupId: activeGrpId,
    });

    // Get the new node and connect it
    const updatedNodes = useDiagramStore.getState().nodes;
    const newNode = updatedNodes[updatedNodes.length - 1];
    if (activeGrpId && newNode) st.addNodeToGroup(newNode.id, activeGrpId);
    if (newNode) {
      const targetSocket = newNode.sockets.find(s => s.type === 'input') ?? newNode.sockets[0];
      if (targetSocket) {
        st.completeConnection(newNode.id, targetSocket.id);
      } else {
        st.cancelConnection();
      }
    } else {
      st.cancelConnection();
    }
    setQuickNodeCreation(null);
    lastAddedNodeId.current = newNode?.id ?? null;
  }, [quickNodeCreation, connectionState.isConnecting, connectionState.tempEdge, connectionState.sourceNodeId, connectionState.sourceSocketId, snapshotBeforeMutation, toCanvas, interactionPolicy.canMutateStructure]);

  // ── Context menus ────────────────────────────────────────────────────────
  const handleNodeContextMenu = useCallback((nodeId: string, x: number, y: number) => {
    if (isViewOnly) return;
    setContextMenu({ type: 'node', x, y, nodeId });
  }, [isViewOnly]);
  const handleEdgeContextMenu = useCallback((edgeId: string, x: number, y: number) => {
    if (isViewOnly) return;
    setContextMenu({ type: 'edge', x, y, edgeId });
  }, [isViewOnly]);
  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // ── Group actions ────────────────────────────────────────────────────────
  const handleGroupSelected = useCallback(() => {
    if (!interactionPolicy.canMutateStructure) return;
    // Collect directly selected node IDs (excluding group nodes which have childGraphId)
    const directNodeIds = selectedItems.filter(id => {
      const node = nodes.find(n => n.id === id);
      return node && !node.childGraphId && !isMarkupType(node.type);
    });
    
    if (directNodeIds.length >= 2) {
      usedViaPointer('group-nodes');
      snapshotBeforeMutation();
      createGroup(directNodeIds);
      // Selection is updated by createGroup to select the new group node
    }
  }, [selectedItems, nodes, createGroup, snapshotBeforeMutation, interactionPolicy.canMutateStructure]);

  // Wrapper: when a node is updated during drag MOVE — only update position, no bounds recalc
  // (recalc on every move causes drift in nested groups)
  const handleNodeUpdate = useCallback((id: string, updates: Partial<DiagramNode>) => {
    if (!interactionPolicy.canDragNodes && updates.position) return;
    updateNode(id, updates);
    // Frame membership and group bounds recalc only happen on drag END (see handleMultiDragEnd)
    // For single-node drags, NodeComponent calls onUpdate on both move and end —
    // we detect end by absence of ongoing drag via a deferred recalc
    if (updates.position) {
      const node = nodes.find(n => n.id === id);

      if (node?.parentGroupId) {
        const pgId = node.parentGroupId;
        const pg = groups.find(g => g.id === pgId);
        if (pg && !pg.isFrame) {
          recalcGroupBounds(pgId);
        }
      }
      // Check frame membership after position update (deferred to avoid flicker during drag)
      const node2 = nodes.find(n => n.id === id);
      if (node2) checkFrameMembership(id, updates.position!, node2.size);
    }
  }, [updateNode, nodes, groups, recalcGroupBounds, checkFrameMembership, interactionPolicy.canDragNodes]);

  // ── Waypoint drag helpers ────────────────────────────────────────────────
  const { waypointCandidateEdgeId, handleWaypointDragMove, handleWaypointDragEnd } =
    useWaypointDrag(edges, nodes, rKeyHeld);

  const handleNodeResize = useCallback((id: string, newWidth: number, newHeight: number, options?: { fromCenter?: boolean; cascade?: boolean }) => {
    if (!interactionPolicy.canMutateStyles) return;
    const node = useDiagramStore.getState().nodes.find(n => n.id === id);
    if (!node) return;

    const clampBox = (target: DiagramNode, width: number, height: number) => {
      if (isMarkupType(target.type)) {
        if (isStrokeMarkup(target.type)) return target.size;
        const fitted = target.type === 'text' ? fitTextNodeSize(target.label || '', 40) : null;
        const minW = fitted?.width ?? (target.type === 'text' ? 40 : 80);
        const minH = fitted?.height ?? (target.type === 'text' ? 24 : 60);
        return { width: Math.max(minW, Math.round(width)), height: Math.max(minH, Math.round(height)) };
      }
      if (isMagneticNode(target.type)) {
        const fitted = constrainedResize(
          target.type,
          target.label,
          Math.round(width),
          Math.round(height),
          { w: target.size.width, h: target.size.height },
          locksEqualAspect(target.type),
        );
        let w = fitted.width;
        let h = fitted.height;
        if (UML_CLASSIFIER_TYPES.has(target.type)) {
          const floor = classifierBoxSize(target);
          w = Math.max(w, floor.width);
          h = Math.max(h, floor.height);
        }
        return { width: w, height: h };
      }
      return { width: Math.max(140, Math.round(width)), height: Math.max(80, Math.round(height)) };
    };

    const commitBox = (target: DiagramNode, size: { width: number; height: number }, position: Point) => {
      if (isMarkupType(target.type)) {
        if (isStrokeMarkup(target.type)) return;
        updateNode(target.id, { size, position });
      } else if (isMagneticNode(target.type)) {
        const rot = (target.data?.shapeRotation as number) ?? 0;
        const newSockets = target.type === 'box' && !socketsMatchFourSided(target.sockets)
          ? scaleSocketsToSize(target.sockets, target.size.width, target.size.height, size.width, size.height)
          : rebuildSketchSockets(target.type, size.width, size.height, rot, target.sockets);
        updateNode(target.id, { size, position, sockets: newSockets });
      } else {
        const newSockets = target.sockets.map(s =>
          s.type === 'output' ? { ...s, position: { ...s.position, x: size.width } } : s
        );
        updateNode(target.id, { size, position, sockets: newSockets });
      }
      if (target.parentGroupId) recalcGroupBounds(target.parentGroupId);
    };

    const size = clampBox(node, newWidth, newHeight);
    const next = options?.fromCenter
      ? centeredResize(node, size.width, size.height, { x: true, y: true })
      : { size, position: node.position };
    commitBox(node, next.size, next.position);

    if (!options?.cascade || !isStackableDrawing(node.type)) return;
    const snap = getStackSnap(node);
    if (!snap) return;
    const dW = next.size.width - node.size.width;
    const dH = next.size.height - node.size.height;
    const patches = collectSnappedAncestorPatches(id, dW, dH, useDiagramStore.getState().nodes);
    for (const patch of patches) {
      const ancestor = useDiagramStore.getState().nodes.find(n => n.id === patch.id);
      if (!ancestor) continue;
      const clamped = clampBox(ancestor, patch.size.width, patch.size.height);
      commitBox(ancestor, clamped, {
        x: patch.position.x + (patch.size.width - clamped.width) / 2,
        y: patch.position.y + (patch.size.height - clamped.height) / 2,
      });
    }
  }, [updateNode, recalcGroupBounds, interactionPolicy.canMutateStyles]);

  const handleRotateGeneralShape = useCallback((id: string) => {
    if (!interactionPolicy.canMutateStyles) return;
    const node = nodes.find(n => n.id === id);
    if (!node || !GENERAL_SHAPE_TYPES.has(node.type)) return;
    const newRotation = (((node.data?.shapeRotation as number) ?? 0) + 90) % 360;
    const { width: w, height: h } = node.size;
    let nextSize = node.size;
    let newSockets;
    if (node.type === 'triangle') {
      newSockets = buildTriangleSocketsRotated(w, h, newRotation);
    } else if (node.type === 'hexagon') {
      newSockets = buildHexagonSocketsRotated(w, h, newRotation);
    } else if (node.type === 'diamond') {
      nextSize = { width: h, height: w };
      newSockets = buildFourSidedSockets(nextSize.width, nextSize.height);
    } else {
      newSockets = buildFourSidedSockets(w, h);
    }
    snapshotBeforeMutation();
    updateNode(id, { size: nextSize, sockets: newSockets, data: { ...node.data, shapeRotation: newRotation } });
    if (node.parentGroupId) recalcGroupBounds(node.parentGroupId);
  }, [nodes, updateNode, recalcGroupBounds, snapshotBeforeMutation, interactionPolicy.canMutateStyles]);

  const handleRotateNodeDirection = useCallback((id: string) => {
    if (!interactionPolicy.canMutateStyles) return;
    usedViaPointer('rotate-direction');
    const node = nodes.find(n => n.id === id);
    if (!node) return;
    const SHAPED = new Set(['diamond', 'database', 'queue', 'waypoint', 'group_input', 'group_output']);
    if (SHAPED.has(node.type) || isMagneticNode(node.type)) return;
    const currentDir = (node.data?.direction as 'ltr' | 'ttb' | 'rtl') ?? 'ltr';
    const cycle: Array<'ltr' | 'ttb' | 'rtl'> = ['ltr', 'ttb', 'rtl'];
    const nextDir = cycle[(cycle.indexOf(currentDir) + 1) % cycle.length];
    const nIn = node.sockets.filter(s => s.type === 'input').length;
    const nOut = node.sockets.filter(s => s.type === 'output').length;
    const nodeH = calcNodeHeight(nIn, nOut);
    const sockets = buildSocketsForDirection(node.type, node.size.width, nodeH, nextDir);
    const newSockets = sockets.map(ns => {
      const old = node.sockets.find(os => os.id === ns.id);
      return old ? { ...ns, label: old.label } : ns;
    });
    snapshotBeforeMutation();
    updateNode(id, { sockets: newSockets, data: { ...node.data, direction: nextDir } });
    if (node.parentGroupId) recalcGroupBounds(node.parentGroupId);
  }, [nodes, updateNode, recalcGroupBounds, snapshotBeforeMutation, interactionPolicy.canMutateStyles]);

  const handleNodeRename = useCallback((id: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    usedViaPointer('rename');
    setInlineEdit({ nodeId: id, field: 'label' });
  }, [interactionPolicy.canMutateStructure]);

  const handleAddSocketWithRename = useCallback((nodeId: string, direction: 'input' | 'output') => {
    if (!interactionPolicy.canMutateStructure) return;
    usedViaPointer(direction === 'input' ? 'add-socket-in' : 'add-socket-out');
    const socketId = addSocket(nodeId, direction);
    if (socketId) {
      // Trigger inline socket naming
      setInlineSocketEdit({ nodeId, socketId });
    }
  }, [addSocket, interactionPolicy.canMutateStructure]);

  const handleGroupRename = useCallback((id: string) => {
    if (!interactionPolicy.canMutateStructure) return;
    setInlineGroupEdit({ groupId: id });
  }, [interactionPolicy.canMutateStructure]);

  const handleGroupDragMove = useCallback((id: string, dx: number, dy: number) => {
    const group = groups.find(g => g.id === id);
    if (!group) return;
    if (isViewOnly && !group.isFrame) return;
    // Move all direct child nodes
    group.childNodeIds.forEach(nid => {
      const node = nodes.find(n => n.id === nid);
      if (node) {
        updateNode(nid, { position: { x: node.position.x + dx, y: node.position.y + dy } });
      }
    });
    // Move child sub-groups recursively
    const moveSubGroup = (sgId: string, ddx: number, ddy: number) => {
      const sg = groups.find(g => g.id === sgId);
      if (!sg) return;
      updateGroup(sgId, { position: { x: sg.position.x + ddx, y: sg.position.y + ddy } });
      sg.childNodeIds.forEach(nid => {
        const node = nodes.find(n => n.id === nid);
        if (node) updateNode(nid, { position: { x: node.position.x + ddx, y: node.position.y + ddy } });
      });
      sg.childGroupIds.forEach(cgid => moveSubGroup(cgid, ddx, ddy));
    };
    group.childGroupIds.forEach(cgid => moveSubGroup(cgid, dx, dy));
    // Move group frame itself
    const newPos = { x: group.position.x + dx, y: group.position.y + dy };
    updateGroup(id, { position: newPos });
    // Recalc parent group bounds if this is a nested group (non-frame parent)
    if (group.parentGroupId) {
      const parentGroup = groups.find(g => g.id === group.parentGroupId);
      if (parentGroup && !parentGroup.isFrame) recalcGroupBounds(group.parentGroupId);
    }
    // For non-frame groups, check if they moved into/out of a frame
    if (!group.isFrame) {
      checkGroupFrameMembership(id, newPos, group.size);
    }
  }, [groups, nodes, updateNode, updateGroup, recalcGroupBounds, checkGroupFrameMembership, isViewOnly]);

  // ── Edge waypoints ───────────────────────────────────────────────────────
  const handleWaypointUpdate = useCallback((edgeId: string, waypoints: Point[]) => {
    if (!interactionPolicy.canMutateStructure) return;
    updateEdge(edgeId, { waypoints });
  }, [updateEdge, interactionPolicy.canMutateStructure]);

  const handleEdgeMessageUpdate = useCallback((edgeId: string, messages: import('./types').EdgeMessage[]) => {
    if (!interactionPolicy.canMutateStyles) return;
    snapshotBeforeMutation();
    updateEdge(edgeId, { messages });
  }, [updateEdge, interactionPolicy.canMutateStyles, snapshotBeforeMutation]);

  const handleEdgeMessageDblClick = useCallback((edgeId: string, msgId: string, canvasCX: number, canvasCY: number, canvasBoxW: number, canvasBoxH: number) => {
    if (!interactionPolicy.canMutateStyles) return;
    setInlineEdgeEdit({ edgeId, msgId, canvasCX, canvasCY, canvasBoxW, canvasBoxH });
  }, [interactionPolicy.canMutateStyles]);

  const handleAddAnchor = useCallback((edgeId: string, screenX: number, screenY: number) => {
    if (!interactionPolicy.canMutateStructure) return;
    const edge = edges.find(e => e.id === edgeId);
    if (!edge) return;
    const src = nodes.find(n => n.id === edge.sourceNodeId);
    const tgt = nodes.find(n => n.id === edge.targetNodeId);
    if (!src || !tgt) return;
    const srcSocket = src.sockets.find(s => s.id === edge.sourceSocketId);
    const tgtSocket = tgt.sockets.find(s => s.id === edge.targetSocketId);
    if (!srcSocket || !tgtSocket) return;

    const click = toCanvas(screenX, screenY);
    const x0 = src.position.x + srcSocket.position.x;
    const y0 = src.position.y + srcSocket.position.y;
    const x1 = tgt.position.x + tgtSocket.position.x;
    const y1 = tgt.position.y + tgtSocket.position.y;

    // Project click onto source→target axis to determine insertion order
    const ddx = x1 - x0, ddy = y1 - y0;
    const len2 = ddx * ddx + ddy * ddy;
    const tClick = len2 > 0 ? ((click.x - x0) * ddx + (click.y - y0) * ddy) / len2 : 0;

    const existing = edge.waypoints ?? [];
    const withT = existing.map(wp => {
      const t = len2 > 0 ? ((wp.x - x0) * ddx + (wp.y - y0) * ddy) / len2 : 0;
      return { wp, t };
    });
    const sorted = [...withT, { wp: click, t: tClick }]
      .sort((a, b) => a.t - b.t)
      .map(item => item.wp);

    updateEdge(edgeId, { waypoints: sorted });
  }, [edges, nodes, toCanvas, updateEdge, interactionPolicy.canMutateStructure]);

  // ── Named Stage event handlers (stable refs for CanvasStage props) ────────
  const handleStageMouseMove = useCallback((e: KonvaMouseEvent) => {
    // Socket slide: project cursor to shape boundary and update socket position in real-time
    if (slidingSocketRef.current) {
      const stage = e.target.getStage();
      const pointer = stage?.getPointerPosition();
      if (pointer) {
        const c = toCanvas(pointer.x, pointer.y);
        const { nodeId, socketId } = slidingSocketRef.current;
        const node = useDiagramStore.getState().nodes.find(n => n.id === nodeId);
        if (node && isMagneticNode(node.type)) {
          const localX = c.x - node.position.x;
          const localY = c.y - node.position.y;
          const projected = projectToGeneralShapeBoundary(
            node.type, node.size.width, node.size.height,
            (node.data?.shapeRotation as number) ?? 0, localX, localY,
          );
          const newSockets = node.sockets.map(s =>
            s.id === socketId ? { ...s, position: projected } : s
          );
          updateNode(nodeId, { sockets: newSockets });
        }
      }
      return;
    }
    handleCanvasMouseMove(e);
    if (
      e.target === e.target.getStage() &&
      (hoveredNodeId || hoveredParticipantId || hoveredMessageId)
    ) {
      setSeqMsgTooltip(null);
      resetSidebarHoverState();
    }
  }, [handleCanvasMouseMove, hoveredNodeId, hoveredParticipantId, hoveredMessageId, resetSidebarHoverState, toCanvas, updateNode]);

  const handleStageMouseUp = useCallback((e: KonvaMouseEvent) => {
    if (slidingSocketRef.current) {
      slidingSocketRef.current = null;
      return;
    }
    handleCanvasMouseUp(e);
  }, [handleCanvasMouseUp]);

  const handleStageMouseLeave = useCallback(() => {
    setSeqMsgTooltip(null);
    resetSidebarHoverState();
  }, [resetSidebarHoverState]);

  const handleStageClick = useCallback((e: KonvaMouseEvent) => {
    handleCanvasClick(e);
    if (e.target === e.target.getStage()) setInfoPanelNodeId(null);
  }, [handleCanvasClick, setInfoPanelNodeId]);

  // ── Save / Load / New ────────────────────────────────────────────────────
  const { handleSave, handleSaveAs, handleLoad, handleApplyJson } =
    usePersistence({
      diagramType, participants, messages, fragments, participantGroups,
      nodes, edges, viewport, layers, groups, pages, activePage, graphs, catalog,
      setDiagramType, clearDiagram, loadDiagram, setJsonError, jsonText,
      suggestedBaseName: projectFileStem,
    });
  const hasDiagramContent =
    nodes.length > 0 ||
    edges.length > 0 ||
    groups.length > 0 ||
    participants.length > 0 ||
    messages.length > 0 ||
    fragments.length > 0 ||
    participantGroups.length > 0;

  const { tempEdgePoints, jumperPoints, forceAdjustedCPs, orthoMidShifts, orthoPaths, edgeTints } = useConnectionGeometry({
    connectionState,
    edges,
    nodes,
    groups,
    defaultRoutingMode: pageRoutingMode,
    forceLayoutEdges,
    tintEdgesByDestination: tintEdgesByDestination ?? true,
  });

  const activeFlow = activeFlowId ? flows.find(f => f.id === activeFlowId) ?? null : null;
  const { getNodeLayerOpacity, isEdgeVisible, getEdgeOpacity, isFlowEdgeAnimated } =
    useFlowLayerVisibility({ layers, activeFlow, nodes });

  const connectedSockets = getConnectedSockets();
  const stageCursor = isPanning
    ? 'grabbing'
    : canvasDrawTool !== 'none' || isFrameDrawMode
    ? 'crosshair'
    : connectionState.isConnecting
    ? 'crosshair'
    : marqueeStart.current
    ? 'crosshair'
    : 'grab';

  // ── Agent bridge — exposes window.__velostruct__ for AI agent control ────
  useVelostructBridge();
  useMcpDirectClient();

  // ── Proxy socket computation for collapsed groups ─────────────────────
  const { proxySocketMap, proxySocketLookup, collapsedChildIds } =
    useProxySocketMap({ groups, nodes, edges });

  const { isInsideGroup, activeGroupId, groupViewPrimaryIds, groupViewEdgeIds } =
    useGroupViewFilter({ groups, groupNavStack, graphNavStack });

  // ── Shared glass panel style ─────────────────────────────────────────────
  const glass: React.CSSProperties = { background: palette.panelBg, borderColor: palette.panelBorder };
  const btnBase = `px-3 py-1.5 rounded-lg text-xs font-medium transition-colors`;
  const btnGhost = `${btnBase} hover:bg-[var(--btn-hover-bg)]`;

  // ── Info panel node ──────────────────────────────────────────────────────
  const {
    infoNode,
    meta,
    localDesc, setLocalDesc,
    localTooltip, setLocalTooltip,
    infoEndpoints,
    handleMetaChange,
    handleAddEndpoint,
    handleRemoveEndpoint,
    handleEndpointChange,
    handleAddLink,
    handleRemoveLink,
    handleLinkChange,
  } = useNodeInfoPanel({ infoPanelNodeId, nodes, updateNodeMeta, updateNode });

  // Check if 2+ items (nodes or groups) are selected for grouping
  const selectedNodeIds = selectedItems.filter(id => nodes.some(n => n.id === id));
  const selectedGroupCount = selectedItems.filter(id => groups.some(g => g.id === id)).length;
  const canGroupSelected = (selectedNodeIds.length + selectedGroupCount) >= 2;

  return (
    <div className="w-full h-screen overflow-hidden relative" style={{ background: canvasPalette.canvasBg }}>
      <style>{`
        .tab-panel {
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .tab-panel-active {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
        .tab-panel-inactive {
          opacity: 0;
          transform: translateY(6px);
          pointer-events: none;
          position: absolute;
          inset: 0;
        }
      `}</style>

      {/* ── Single Konva Stage ──────────────────────────────── */}
      <CanvasStage
        stageRef={stageRef}
        viewport={viewport}
        windowSize={windowSize}
        palette={canvasPalette}
        stageCursor={stageCursor}
        marqueeRect={marqueeRect}
        lassoPoints={lassoPoints}
        frameDrawRect={frameDrawRect}
        drawRectKind={(canvasDrawTool === 'box' || canvasStampType) ? 'box' : 'frame'}
        markupStroke={markupStroke ? { ...markupStroke, kind: 'arrow' } : null}
        onMouseDown={handleStageMouseDown}
        onMouseMove={handleStageMouseMove}
        onMouseUp={handleStageMouseUp}
        onMouseLeave={handleStageMouseLeave}
        onClick={handleStageClick}
        onWheel={handleWheel}
      >

              {/* ── FLOW DIAGRAM RENDERING ── */}
              {diagramType === 'flow' && (
                <FlowCanvas
                  isInsideGroup={isInsideGroup}
                  activeGroupId={activeGroupId}
                  groups={groups}
                  nodes={nodes}
                  edges={edges}
                  layers={layers}
                  selectedItems={selectedItems}
                  proxySocketMap={proxySocketMap}
                  proxySocketLookup={proxySocketLookup}
                  collapsedChildIds={collapsedChildIds}
                  groupViewEdgeIds={groupViewEdgeIds}
                  groupViewPrimaryIds={groupViewPrimaryIds}
                  dragOverridePos={dragOverridePos}
                  connectionState={connectionState}
                  tempEdgePoints={tempEdgePoints}
                  viewport={viewport}
                  palette={palette}
                  isViewOnly={isViewOnly}
                  interactionPolicy={interactionPolicy}
                  activeFlow={activeFlow}
                  inlineEdit={inlineEdit}
                  inlineSocketEdit={inlineSocketEdit}
                  anchorNodeId={anchorNodeId}
                  connectedSockets={connectedSockets}
                  waypointCandidateEdgeId={waypointCandidateEdgeId}
                  discoveryMode={discoveryMode}
                  iconStyle={iconStyle}
                  socketLabelVisibility={socketLabelVisibility}
                  defaultRoutingMode={pageRoutingMode}
                  textScale={pageTextScale}
                  jumperPoints={jumperPoints}
                  forceAdjustedCPs={forceAdjustedCPs}
                  orthoMidShifts={orthoMidShifts}
                  orthoPaths={orthoPaths}
                  edgeTints={edgeTints}
                  infoPanelNodeId={infoPanelNodeId}
                  selectItem={selectItem}
                  handleGroupRename={handleGroupRename}
                  setContextMenu={setContextMenu}
                  handleGroupDragMove={handleGroupDragMove}
                  snapshotBeforeMutation={snapshotBeforeMutation}
                  updateGroup={updateGroup}
                  enterGroup={enterGroup}
                  handleEdgeContextMenu={handleEdgeContextMenu}
                  handleWaypointUpdate={handleWaypointUpdate}
                  handleEdgeMessageUpdate={handleEdgeMessageUpdate}
                  handleEdgeMessageDblClick={handleEdgeMessageDblClick}
                  inlineEdgeMsgId={inlineEdgeEdit?.msgId ?? null}
                  setInlineEdgeEdit={setInlineEdgeEdit}
                  handleNodeUpdate={handleNodeUpdate}
                  handleSocketMouseDown={handleSocketMouseDown}
                  handleSocketMouseUp={handleSocketMouseUp}
                  handleSocketDoubleClick={handleSocketDoubleClick}
                  handleSocketSlideStart={handleSocketSlideStart}
                  handleNodeRename={handleNodeRename}
                  handleNodeContextMenu={handleNodeContextMenu}
                  handleNodeResize={handleNodeResize}
                  setInlineEdit={setInlineEdit}
                  handleMultiDragStart={handleMultiDragStart}
                  handleMultiDragMove={handleMultiDragMove}
                  handleMultiDragEnd={handleMultiDragEnd}
                  handleWaypointDragMove={handleWaypointDragMove}
                  handleWaypointDragEnd={handleWaypointDragEnd}
                  handleSidebarHoverStart={handleSidebarHoverStart}
                  handleSidebarHoverEnd={handleSidebarHoverEnd}
                  isEdgeVisible={isEdgeVisible}
                  getNodeLayerOpacity={getNodeLayerOpacity}
                  getEdgeOpacity={getEdgeOpacity}
                  isFlowEdgeAnimated={isFlowEdgeAnimated}
                  setInfoPanelNodeId={setInfoPanelNodeId}
                  setColorPicker={setColorPicker}
                  switchPageWithHistory={switchPageWithHistory}
                  setLinkPageForNodeId={setLinkPageForNodeId}
                  setIsPageManagerOpen={setIsPageManagerOpen}
                  enterGraph={enterGraph}
                  connectionSourceSocketType={connectionSourceSocketType}
                  onGhostSocketDrop={handleGhostSocketDrop}
                  onMagneticConnectStart={handleMagneticConnectStart}
                  onMagneticConnectDrop={handleMagneticConnectDrop}
                  connectModifierHeldRef={eKeyHeld}
                />
              )}

              {/* ── SEQUENCE DIAGRAM RENDERING ── */}
              {diagramType === 'sequence' && (() => {
                const seqRefs: SequenceCanvasRefs = {
                  isDraggingParticipantRef,
                  isDraggingMessageRef,
                  isDraggingFragmentRef,
                  activeFragmentResizeRef,
                  activeAltDividerDragRef,
                  fragmentResizeBaselineRef,
                  altDividerDragBaselineRef,
                  messageDragBaselineRef,
                  dragReorderSnapshotRef,
                  dragSelectedMessageIdsRef,
                  dragStartMessageYRef,
                  reorderMessageDragKeyRef,
                };
                const seqCallbacks: SequenceCanvasCallbacks = {
                  selectItem,
                  setContextMenu,
                  setInfoPanelFragmentId,
                  setInfoPanelParticipantId,
                  snapshotBeforeMutation,
                  updateParticipant,
                  reorderParticipants,
                  moveFragment,
                  setFragmentDropParentId,
                  setInlineEdit,
                  setInlineEdgeEdit,
                  setSeqMsgTooltip,
                  handleSidebarHoverStart,
                  handleSidebarHoverEnd,
                  handleLifelineMouseDown,
                  handleLifelineMouseUp,
                  getSequenceMessageFootprint,
                };
                const seqDragCallbacks: SequenceCanvasDragCallbacks = {
                  captureSequenceFragmentDragBaseline,
                  restoreSequenceFragmentDragBaseline,
                  beginSequenceDragPerf,
                  recordSequenceDragPerf,
                  endSequenceDragPerf,
                  scheduleFragmentResizeMove,
                  cancelScheduledFragmentResizeMove,
                  applyFragmentEndDrag,
                  scheduleAltDividerDragMove,
                  cancelScheduledAltDividerDragMove,
                  applyAltDividerDrag,
                };
                const seqMsgDragCallbacks: SequenceCanvasMessageDragCallbacks = {
                  applyMessageDragWithReorder,
                  applyMessageDragWithPush,
                  ensureDraggedMessageFragmentExtents,
                  finalizeDraggedMessages,
                  resolveDraggedMessageIds,
                };
                return (
                  <SequenceCanvas
                    participants={participants}
                    messages={messages}
                    fragments={fragments}
                    participantGroups={participantGroups}
                    layers={layers}
                    selectedItems={selectedItems}
                    viewport={viewport}
                    windowSize={windowSize}
                    palette={palette}
                    stickyMode={stickyMode}
                    stickyTopHeaderCanvasY={stickyTopHeaderCanvasY}
                    stickyBottomHeaderCanvasY={stickyBottomHeaderCanvasY}
                    stickyMaskBands={{
                      top: stickyMaskBands.top ?? undefined,
                      bottom: stickyMaskBands.bottom ?? undefined,
                    }}
                    sequenceLifelineBottom={sequenceLifelineBottom}
                    inlineEdit={inlineEdit}
                    inlineEdgeEdit={inlineEdgeEdit}
                    infoPanelParticipantId={infoPanelParticipantId}
                    sequenceDrag={sequenceDrag}
                    fragmentDropParentId={fragmentDropParentId}
                    isViewOnly={isViewOnly}
                    interactionPolicy={interactionPolicy}
                    refs={seqRefs}
                    callbacks={seqCallbacks}
                    dragCallbacks={seqDragCallbacks}
                    messageDragCallbacks={seqMsgDragCallbacks}
                  />
                );
              })()}


      </CanvasStage>

      {/* ── Header ──────────────────────────────────────────── */}
      {isHeaderVisible && !isDistractionFree && (
        <header className="absolute top-4 left-1/2 -translate-x-1/2 z-50 select-none" data-tour="header">
          <div style={glass} className="relative backdrop-blur-md border rounded-2xl px-6 py-3 shadow-2xl flex items-center gap-4">
            <div className="pointer-events-none">
              <h1 style={{ color: palette.panelText }} className="text-base font-bold tracking-tight">VeloStruct</h1>
              <p style={{ color: palette.panelSubText }} className="text-xs">
                {activeProjectName} - System Architecture {isViewOnly ? 'Viewer' : 'Editor'}
              </p>
            </div>
            <button
              onClick={() => setIsHeaderVisible(false)}
              className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors flex-shrink-0 text-xs"
              style={{ color: palette.panelSubText }}
              title="Hide header"
            >✕</button>
          </div>
        </header>
      )}

      {/* ── Top-right toolbar ─────────────────────────────────── */}
      <TopToolbar
        pagesLength={pages.length}
        isViewOnly={isViewOnly}
        hasDiagramContent={hasDiagramContent}
        showLocalDevTools={showLocalDevTools}
        isJsonPanelOpen={isJsonPanelOpen}
        isDistractionFree={isDistractionFree}
        canGroupSelected={canGroupSelected}
        selectedNodeIdsLength={selectedNodeIds.length}
        isToolbarExpanded={isToolbarExpanded}
        setIsToolbarExpanded={setIsToolbarExpanded}
        toolbarCollapseTimer={toolbarCollapseTimer}
        setToolbarCollapseTimer={setToolbarCollapseTimer}
        openDropdown={openDropdown}
        setOpenDropdown={setOpenDropdown}
        handleCreateProject={handleCreateProject}
        handleSave={handleSave}
        handleSaveAs={handleSaveAs}
        handleLoad={() => { window.showOpenFilePicker ? handleLoad() : fileInputRef.current?.click(); }}
        handlePublishLink={handlePublishLink}
        setExportAllPagesAsPng={setExportAllPagesAsPng}
        setIsPngExportModalOpen={setIsPngExportModalOpen}
        handleExportImage={handleExportImage}
        setIsProjectManagerOpen={setIsProjectManagerOpen}
        setIsPageManagerOpen={setIsPageManagerOpen}
        setIsMermaidModalOpen={setIsMermaidModalOpen}
        setIsJsonPanelOpenHandler={setIsJsonPanelOpen}
        setIsDevStateViewerOpen={setIsDevStateViewerOpen}
        setIsSettingsOpen={setIsSettingsOpen}
        setIsFeedbackOpen={setIsFeedbackOpen}
        setIsChordModalOpen={setIsChordModalOpen}
        setIsDistractionFreeHandler={setIsDistractionFree}
        handleGroupSelected={handleGroupSelected}
        onStartTutorial={() => useOnboardingStore.getState().startTutorial()}
        fileInputRef={fileInputRef}
        glassStyle={glass}
        btnGhostClass={btnGhost}
        palette={palette}
      />

      {/* ── JSON editor panel ─────────────────────────────────── */}
      {showLocalDevTools && isJsonPanelOpen && !infoNode && (
        <div className="absolute right-4 top-16 bottom-16 z-40 w-80 flex flex-col rounded-2xl shadow-2xl overflow-hidden border backdrop-blur-md" style={glass}>
          <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0" style={{ borderColor: palette.panelBorder }}>
            <span style={{ color: palette.panelSubText }} className="text-sm font-semibold">JSON Editor</span>
            <span style={{ color: palette.panelSubText }} className="text-xs">AI-ready format</span>
          </div>
          <textarea
            className="flex-1 bg-transparent text-xs font-mono p-3 resize-none focus:outline-none leading-relaxed"
            style={{ color: palette.panelSubText }}
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
            spellCheck={false}
          />
          {jsonError && (
            <div className="px-3 py-2 text-red-400 text-xs bg-red-500/10 border-t border-red-500/20 flex-shrink-0">{jsonError}</div>
          )}
          <div className="px-3 py-2.5 border-t flex-shrink-0" style={{ borderColor: palette.panelBorder }}>
            <button
              onClick={handleApplyJson}
              className="w-full py-1.5 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ background: '#4f46e5' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#4338ca')}
              onMouseLeave={e => (e.currentTarget.style.background = '#4f46e5')}
            >Apply</button>
          </div>
        </div>
      )}

      {/* ── Node info panel ───────────────────────────────────── */}
      {infoNode && (
        <div className="absolute right-4 top-16 bottom-16 z-50 w-80 flex flex-col rounded-2xl shadow-2xl overflow-hidden border backdrop-blur-md" style={glass}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0" style={{ borderColor: palette.panelBorder }}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: infoNode.data?.color ?? '#6366f1' }} />
              <span style={{ color: palette.panelSubText }} className="text-sm font-semibold truncate">{infoNode.label}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider flex-shrink-0" style={{ background: (infoNode.data?.color ?? '#6366f1') + '20', color: infoNode.data?.color ?? '#6366f1' }}>{infoNode.type}</span>
            </div>
            <button onClick={() => setInfoPanelNodeId(null)} className="transition-colors ml-2 flex-shrink-0" style={{ color: palette.panelSubText }}>✕</button>
          </div>

          <div key={infoPanelNodeId} className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Status badge */}
            {meta.status && (
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[meta.status] ?? '#94a3b8' }} />
                <span className="text-xs capitalize" style={{ color: STATUS_COLORS[meta.status] ?? '#94a3b8' }}>{meta.status}</span>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Name</label>
              <input
                type="text"
                className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                value={infoNode.label}
                onChange={e => updateNode(infoPanelNodeId!, { label: e.target.value })}
              />
            </div>

            {UML_CLASSIFIER_TYPES.has(infoNode.type) && (
              <>
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Stereotype</label>
                  <input
                    type="text"
                    className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
                    style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                    value={typeof infoNode.data?.stereotype === 'string' ? infoNode.data.stereotype : ''}
                    placeholder="interface, enumeration…"
                    onChange={e => updateNode(infoPanelNodeId!, { data: { ...infoNode.data, stereotype: e.target.value } })}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>
                    {infoNode.type === 'uml_enum' ? 'Values' : 'Attributes'}
                  </label>
                  <UmlMemberEditor
                    mode="field"
                    lines={Array.isArray(infoNode.data?.attributes) ? infoNode.data.attributes as string[] : []}
                    allowType={infoNode.type !== 'uml_enum'}
                    palette={palette}
                    onChange={lines => updateNode(infoPanelNodeId!, { data: { ...infoNode.data, attributes: lines } })}
                  />
                </div>
                {infoNode.type !== 'uml_enum' && (
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Methods</label>
                  <UmlMemberEditor
                    mode="method"
                    lines={Array.isArray(infoNode.data?.methods) ? infoNode.data.methods as string[] : []}
                    allowType
                    palette={palette}
                    onChange={lines => updateNode(infoPanelNodeId!, { data: { ...infoNode.data, methods: lines } })}
                  />
                </div>
                )}
              </>
            )}

            {infoNode.catalogId && (() => {
              const entry = catalog.find(item => item.id === infoNode.catalogId);
              const views = countCatalogViews(
                { catalog, nodes, participants, pages, graphs },
                infoNode.catalogId,
              );
              return (
                <div className="rounded-lg border px-3 py-2 space-y-1.5" style={{ borderColor: palette.panelBorder }}>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: palette.panelSubText }}>
                    Catalog identity
                  </div>
                  <div className="text-xs" style={{ color: palette.panelText }}>
                    {entry?.name ?? infoNode.label}
                    <span className="ml-1.5" style={{ color: palette.panelSubText }}>
                      {views} view{views === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="text-[10px]" style={{ color: palette.panelSubText }}>
                    Packed info syncs to every instance of this object.
                  </p>
                  {!infoNode.linkedPageId && (
                    <button
                      className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                      onClick={() => {
                        snapshotBeforeMutation();
                        const pageId = createChildView(infoPanelNodeId!);
                        if (pageId) switchPageWithHistory(pageId);
                      }}
                    >
                      Create container view
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Color */}
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Accent Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                  value={infoNode.data?.color ?? '#6366f1'}
                  onChange={e => updateNode(infoPanelNodeId!, { data: { ...infoNode.data, color: e.target.value } })}
                />
                <span className="text-xs font-mono" style={{ color: palette.panelSubText }}>{infoNode.data?.color ?? '#6366f1'}</span>
                {/* Quick presets */}
                <div className="flex gap-1 ml-auto">
                  {['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#ef4444', '#8b5cf6'].map(c => (
                    <button key={c} title={c}
                      className="w-4 h-4 rounded-full ring-1 ring-white/20 transition-transform hover:scale-110"
                      style={{ background: c }}
                      onClick={() => updateNode(infoPanelNodeId!, { data: { ...infoNode.data, color: c } })}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Layer */}
            {layers.length > 1 && (
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Layer</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
                  style={{ color: palette.panelText, borderColor: palette.panelBorder, background: palette.panelBg }}
                  value={infoNode.layerId ?? 'default'}
                  onChange={e => setNodeLayer(infoPanelNodeId!, e.target.value === 'default' ? undefined : e.target.value)}
                >
                  {layers.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Description */}
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Description</label>
              <textarea
                className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-indigo-500/60 transition-colors"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                rows={3}
                placeholder="Describe this node…"
                value={localDesc}
                onChange={e => setLocalDesc(e.target.value)}
                onBlur={() => handleMetaChange({ description: localDesc })}
              />
            </div>

            {/* Tooltip — hover-only detail, not shown on canvas */}
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>
                Tooltip <span className="normal-case opacity-60 ml-1 lowercase">· shown on hover in info panel</span>
              </label>
              <textarea
                className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-indigo-500/60 transition-colors"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                rows={2}
                placeholder="Detailed notes visible on hover…"
                value={localTooltip}
                onChange={e => setLocalTooltip(e.target.value)}
                onBlur={() => handleMetaChange({ tooltip: localTooltip || undefined })}
              />
            </div>

            {/* Endpoints (api nodes only) */}
            {infoNode.type === 'api' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-wider" style={{ color: palette.panelSubText }}>Endpoints</label>
                  <button onClick={handleAddEndpoint} className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors">+ Add</button>
                </div>
                <div className="space-y-1.5">
                  {infoEndpoints.map((ep, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <input
                        type="text"
                        className="flex-1 bg-white/5 border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:border-indigo-500/60"
                        style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                        placeholder="GET /api/endpoint"
                        value={ep}
                        onChange={e => handleEndpointChange(i, e.target.value)}
                      />
                      <button onClick={() => handleRemoveEndpoint(i)} className="text-red-400/60 hover:text-red-400 transition-colors flex-shrink-0">✕</button>
                    </div>
                  ))}
                  {infoEndpoints.length === 0 && (
                    <div className="text-xs italic" style={{ color: palette.panelSubText }}>No endpoints defined</div>
                  )}
                </div>
              </div>
            )}

            {/* Owner */}
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Owner</label>
              <input
                type="text"
                className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                placeholder="Team or person…"
                value={meta.owner ?? ''}
                onChange={e => handleMetaChange({ owner: e.target.value })}
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Status</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60 transition-colors"
                style={{ color: palette.panelText, borderColor: palette.panelBorder, background: palette.panelBg }}
                value={meta.status ?? ''}
                onChange={e => handleMetaChange({ status: (e.target.value as NodeMeta['status']) || undefined })}
              >
                <option value="">— Select status —</option>
                <option value="active">Active</option>
                <option value="deprecated">Deprecated</option>
                <option value="planned">Planned</option>
                <option value="legacy">Legacy</option>
              </select>
            </div>

            {/* Sockets — split two-column layout */}
            {(() => {
              const inputSockets = [...infoNode.sockets.filter(s => s.type === 'input')].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
              const outputSockets = [...infoNode.sockets.filter(s => s.type === 'output')].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
              const isBoundary = infoNode.type === 'group_input' || infoNode.type === 'group_output';
              const renderSocketCol = (sockets: typeof inputSockets, dir: 'input' | 'output') => (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: palette.panelSubText }}>
                      {dir === 'input' ? '▼ In' : '▲ Out'}
                    </span>
                    {/* Only allow adding sockets that make sense for this node type */}
                    {(!isBoundary || (infoNode.type === 'group_input' && dir === 'output') || (infoNode.type === 'group_output' && dir === 'input')) && (
                      <button
                        className="text-[10px] leading-none transition-colors hover:opacity-100 opacity-60"
                        style={{ color: dir === 'input' ? '#10b981' : '#8b5cf6' }}
                        onClick={() => handleAddSocketWithRename(infoPanelNodeId!, dir)}
                        title={`Add ${dir} socket`}
                      >+</button>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {sockets.map((socket, i) => (
                      <div
                        key={socket.id}
                        draggable
                        onDragStart={e => { e.dataTransfer.setData('text/plain', socket.id); e.dataTransfer.effectAllowed = 'move'; }}
                        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; (e.currentTarget as HTMLElement).style.borderColor = '#818cf8'; }}
                        onDragLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = palette.panelBorder; }}
                        onDrop={e => {
                          e.preventDefault();
                          (e.currentTarget as HTMLElement).style.borderColor = palette.panelBorder;
                          const draggedId = e.dataTransfer.getData('text/plain');
                          if (draggedId !== socket.id) moveSocket(infoPanelNodeId!, draggedId, i);
                        }}
                        className="flex items-center gap-1.5 px-1.5 py-1 rounded bg-white/5 border transition-colors cursor-grab active:cursor-grabbing"
                        style={{ borderColor: palette.panelBorder }}
                      >
                        <span className="text-[10px] opacity-30 flex-shrink-0 select-none" style={{ color: palette.panelSubText }}>⠿</span>
                        <input
                          type="text"
                          className="flex-1 bg-transparent border-0 text-[11px] focus:outline-none px-0 min-w-0 cursor-text"
                          style={{ color: palette.panelText }}
                          defaultValue={socket.label}
                          onBlur={e => {
                            const val = e.target.value.trim();
                            if (val && val !== socket.label) renameSocket(infoPanelNodeId!, socket.id, val);
                          }}
                          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        />
                      </div>
                    ))}
                    {sockets.length === 0 && (
                      <div className="text-[10px] italic py-1 px-1" style={{ color: palette.panelBorder }}>none</div>
                    )}
                  </div>
                </div>
              );
              return (
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: palette.panelSubText }}>Sockets</label>
                  <div className="flex gap-2">
                    {renderSocketCol(inputSockets, 'input')}
                    <div className="w-px flex-shrink-0" style={{ background: palette.panelBorder }} />
                    {renderSocketCol(outputSockets, 'output')}
                  </div>
                </div>
              );
            })()}

            {/* Connections */}
            {(() => {
              const incoming = edges.filter(e => e.targetNodeId === infoPanelNodeId);
              const outgoing = edges.filter(e => e.sourceNodeId === infoPanelNodeId);
              if (incoming.length === 0 && outgoing.length === 0) return null;
              return (
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: palette.panelSubText }}>Connections</label>
                  <div className="space-y-1">
                    {incoming.map(e => {
                      const src = nodes.find(n => n.id === e.sourceNodeId);
                      const sock = src?.sockets.find(s => s.id === e.sourceSocketId);
                      return (
                        <div key={e.id} className="flex items-center gap-2 text-xs py-1 px-2 rounded" style={{ background: palette.panelBg }}>
                          <span style={{ color: palette.panelBorder }}>←</span>
                          <span className="truncate" style={{ color: palette.panelText }}>{src?.label ?? '?'}</span>
                          {sock?.label && <span className="ml-auto flex-shrink-0 opacity-50" style={{ color: palette.panelSubText }}>{sock.label}</span>}
                        </div>
                      );
                    })}
                    {outgoing.map(e => {
                      const tgt = nodes.find(n => n.id === e.targetNodeId);
                      const sock = infoNode.sockets.find(s => s.id === e.sourceSocketId);
                      return (
                        <div key={e.id} className="flex items-center gap-2 text-xs py-1 px-2 rounded" style={{ background: palette.panelBg }}>
                          <span style={{ color: palette.panelBorder }}>→</span>
                          <span className="truncate" style={{ color: palette.panelText }}>{tgt?.label ?? '?'}</span>
                          {sock?.label && <span className="ml-auto flex-shrink-0 opacity-50" style={{ color: palette.panelSubText }}>{sock.label}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Links */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wider" style={{ color: palette.panelSubText }}>Links</label>
                <button onClick={handleAddLink} className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors">+ Add</button>
              </div>
              <div className="space-y-2">
                {(meta.links ?? []).map((link, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex-1 space-y-1">
                      <input
                        type="text"
                        className="w-full bg-white/5 border rounded px-2 py-1 text-xs focus:outline-none focus:border-indigo-500/60"
                        style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                        placeholder="Label"
                        value={link.label}
                        onChange={e => handleLinkChange(i, 'label', e.target.value)}
                      />
                      <input
                        type="text"
                        className="w-full bg-white/5 border rounded px-2 py-1 text-xs focus:outline-none focus:border-indigo-500/60"
                        style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                        placeholder="https://…"
                        value={link.url}
                        onChange={e => handleLinkChange(i, 'url', e.target.value)}
                      />
                      {link.url && (
                        <a href={link.url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors block truncate">
                          ↗ {link.label || link.url}
                        </a>
                      )}
                    </div>
                    <button onClick={() => handleRemoveLink(i)} className="text-red-400/60 hover:text-red-400 transition-colors mt-1 flex-shrink-0">✕</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Sequence Participant Info Panel ──────────────────────── */}
      {infoPanelParticipantId && (() => {
        const infoP = participants.find(p => p.id === infoPanelParticipantId);
        if (!infoP) return null;
        return (
          <ParticipantInfoPanel
            palette={palette}
            glass={glass}
            participant={infoP}
            pages={pages}
            seqLifelineLength={seqLifelineLength}
            setSeqLifelineLength={setSeqLifelineLength}
            onClose={() => setInfoPanelParticipantId(null)}
            onUpdateParticipant={updateParticipant}
            onRemoveParticipant={removeParticipant}
            onSwitchPage={switchPageWithHistory}
            onRequestLinkPage={(participantId) => {
              setLinkPageForNodeId(participantId);
              setIsPageManagerOpen(true);
            }}
            snapshotBeforeMutation={snapshotBeforeMutation}
          />
        );
      })()}

      {/* ── Sequence Message Info Panel ─────────────────────────── */}
      {infoPanelMessageId && (() => {
        const infoMsg = messages.find(m => m.id === infoPanelMessageId);
        if (!infoMsg) return null;
        return (
          <MessageInfoPanel
            palette={palette}
            glass={glass}
            message={infoMsg}
            participants={participants}
            onClose={() => setInfoPanelMessageId(null)}
            onUpdateMessage={updateMessage}
            onRemoveMessage={removeMessage}
            snapshotBeforeMutation={snapshotBeforeMutation}
          />
        );
      })()}

      {/* ── Sequence Fragment Info Panel ────────────────────────── */}
      {infoPanelFragmentId && (() => {
        const frag = fragments.find(f => f.id === infoPanelFragmentId);
        if (!frag) return null;
        return (
          <FragmentInfoPanel
            palette={palette}
            glass={glass}
            fragment={frag}
            participants={participants}
            onClose={() => setInfoPanelFragmentId(null)}
            onUpdateFragment={updateFragment}
            onRemoveFragment={removeFragment}
            snapshotBeforeMutation={snapshotBeforeMutation}
          />
        );
      })()}

      {/* ── Navigation box (pages + groups) ────────────────────── */}
      <div className={`absolute left-4 top-4 z-40 transition-all duration-300 ${isToolboxOpen && !isDistractionFree ? 'translate-x-0 opacity-100' : '-translate-x-[120%] opacity-0 pointer-events-none'}`}>
        <div style={glass} className="backdrop-blur-md border rounded-2xl shadow-2xl px-3 py-2 flex items-center gap-2" data-tour="page-nav">
          {/* Page navigation */}
          <div className="flex gap-0.5">
            <button
              onClick={() => { usedViaPointer('page-back'); goBack(); }}
              disabled={historyIndex <= 0}
              className="w-6 h-6 rounded flex items-center justify-center text-xs transition-colors hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ color: palette.panelSubText }}
              title="Back to previous page (Cmd/Ctrl+[)"
            >&lt;</button>
            <button
              onClick={() => { usedViaPointer('page-forward'); goForward(); }}
              disabled={historyIndex >= pageHistory.length - 1}
              className="w-6 h-6 rounded flex items-center justify-center text-xs transition-colors hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ color: palette.panelSubText }}
              title="Forward to next page (Cmd/Ctrl+])"
            >&gt;</button>
            <button
              onClick={goHome}
              className="w-6 h-6 rounded flex items-center justify-center text-xs transition-colors hover:bg-white/10"
              style={{ color: palette.panelSubText }}
              title="Home (first page)"
            >H</button>
          </div>
          {/* Group navigation */}
          {graphNavStack.length > 0 && (
            <>
              <div className="w-px h-4" style={{ background: palette.panelBorder }} />
              <button
                onClick={() => exitGraph()}
                className="w-6 h-6 rounded flex items-center justify-center text-xs transition-colors hover:bg-white/10"
                style={{ color: '#f59e0b' }}
                title="Exit group"
              >E</button>
            </>
          )}
        </div>
      </div>

      {/* ── Left sidebar: Toolbox + Layers ────────────────────── */}
      <div className={`absolute left-4 top-20 bottom-16 z-40 flex flex-col gap-3 w-52 transition-all duration-300 ${isToolboxOpen && !isDistractionFree ? 'translate-x-0 opacity-100' : '-translate-x-[120%] opacity-0 pointer-events-none'
        }`}
        data-tour="sidebar"
        onMouseEnter={() => {
          setSeqMsgTooltip(null);
          resetSidebarHoverState();
        }}>
        {/* Unified tabbed sidebar — always visible regardless of diagram type */}
        <LeftSidebar
          isViewOnly={isViewOnly}
          palette={palette}
          glassStyle={glass}
          diagramType={diagramType}
          sidebarTab={sidebarTab}
          setSidebarTab={setSidebarTab}
          setIsToolboxOpen={setIsToolboxOpen}
          autoOpenInfoOnHover={autoOpenInfoOnHover ?? false}
          hoveredNodeId={hoveredNodeId}
          hoveredParticipantId={hoveredParticipantId}
          hoveredMessageId={hoveredMessageId}
          selectedItems={selectedItems}
          viewport={viewport}
          nodes={nodes}
          edges={edges}
          participants={participants}
          messages={messages}
          fragments={fragments}
          pages={pages}
          activePage={activePage}
          snapshotBeforeMutation={snapshotBeforeMutation}
          addParticipant={addParticipant}
          groupParticipants={groupParticipants}
          addFragment={addFragment}
          handleDragFromPanel={handleDragFromPanel}
          isFrameDrawMode={isFrameDrawMode ?? false}
          setIsFrameDrawMode={setIsFrameDrawMode}
          frameDrawStart={frameDrawStart}
          setFrameDrawRect={setFrameDrawRect}
          handleBeautifyLayout={handleBeautifyLayout}
          bKeyHeld={bKeyHeld}
          generalShapesEnabled={generalShapesEnabled ?? false}
          canvasDrawTool={canvasDrawTool}
          setCanvasDrawTool={setCanvasDrawTool}
          canvasStampType={canvasStampType}
          setCanvasStampType={setCanvasStampType}
          kitId={activeKit.id}
          onKitChange={(kit) => {
            snapshotBeforeMutation();
            setPageKit(activePage, kit);
            setEmptyStateDismissed(true);
          }}
          updateEdge={updateEdge}
          removeEdge={removeEdge}
          setInfoPanelNodeId={setInfoPanelNodeId}
          setInfoPanelParticipantId={setInfoPanelParticipantId}
          setInfoPanelMessageId={setInfoPanelMessageId}
          switchPageWithHistory={switchPageWithHistory}
          renamingPageId={renamingPageId}
          setRenamingPageId={setRenamingPageId}
          renamingPageVal={renamingPageVal}
          setRenamingPageVal={setRenamingPageVal}
          handleDeletePage={handleDeletePage}
        />

        {/* Layers / Flows panel */}
        {isLayerPanelOpen && (
          <LayerFlowPanel
            isViewOnly={isViewOnly}
            palette={palette}
            glassStyle={glass}
            bottomPanelTab={bottomPanelTab}
            setBottomPanelTab={setBottomPanelTab}
            pendingLayerEdit={pendingLayerEdit}
            editingLayerId={editingLayerId}
            setEditingLayerId={setEditingLayerId}
            editingFlowId={editingFlowId}
            setEditingFlowId={setEditingFlowId}
            layers={layers}
            activeLayerId={activeLayerId}
            setActiveLayer={setActiveLayer}
            addLayer={addLayer}
            updateLayer={updateLayer}
            removeLayer={removeLayer}
            flows={flows}
            activeFlowId={activeFlowId}
            setActiveFlowId={setActiveFlowId}
            addFlow={addFlow}
            renameFlow={renameFlow}
            updateFlow={updateFlow}
            removeFlow={removeFlow}
            selectedItems={selectedItems}
            nodes={nodes}
            edges={edges}
          />
        )}
      </div>

      {/* ── Flow node glow overlay (CSS box-shadow, true Gaussian blur) ── */}
      {activeFlow && nodes.filter(n => activeFlow.nodeIds.includes(n.id)).map(n => {
        const sx = n.position.x * viewport.scale + viewport.x;
        const sy = n.position.y * viewport.scale + viewport.y;
        const sw = n.size.width * viewport.scale;
        const sh = n.size.height * viewport.scale;
        return (
          <div
            key={`glow-${n.id}`}
            style={{
              position: 'absolute',
              left: sx,
              top: sy,
              width: sw,
              height: sh,
              borderRadius: 10,
              boxShadow: '0 0 16px 5px rgba(129,140,248,0.5)',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
        );
      })}

      {/* Toolbox toggle */}
      {!isToolboxOpen && (
        <button onClick={() => setIsToolboxOpen(true)} style={glass}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-40 backdrop-blur-md border rounded-xl p-3 shadow-2xl hover:border-indigo-500/60 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: palette.panelSubText }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Layers/Flows panel toggle */}
      <button
        onClick={() => setIsLayerPanelOpen(v => !v)}
        style={{ ...(glass as object), borderColor: activeFlowId ? '#818cf880' : undefined }}
        className="absolute left-4 bottom-4 z-40 backdrop-blur-md border rounded-xl px-3 py-1.5 shadow-xl hover:border-indigo-500/60 transition-colors text-xs flex items-center gap-1.5"
      >
        <span style={{ color: activeFlowId ? '#818cf8' : palette.panelSubText }}>
          {activeFlowId ? `◉ ${activeFlow?.name ?? 'Flow'}` : '◧ Layers'}
        </span>
        {(layers.some(l => !l.visible || l.dimmed) || flows.length > 0) && !activeFlowId && (
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 ml-1" />
        )}
      </button>

      {/* Mermaid Exchange toggle */}
      {!isViewOnly && (
        <button
          onClick={() => setIsMermaidModalOpen(true)}
          style={glass}
          className="absolute left-[110px] bottom-4 z-40 backdrop-blur-md border rounded-xl px-3 py-1.5 shadow-xl hover:border-indigo-500/60 transition-colors text-xs flex items-center gap-1.5"
        >
          <span style={{ color: palette.panelSubText }}>m Mermaid</span>
        </button>
      )}

      {/* ── Page Manager floating window ───────────────────── */}
      {isPageManagerOpen && (
        <PageManager
          pages={pages}
          activePage={activePage}
          linkNodeId={isViewOnly ? null : linkPageForNodeId}
          readOnly={isViewOnly}
          onSwitchPage={id => { switchPageWithHistory(id); setLinkPageForNodeId(null); }}
          onLinkNodeToPage={(nodeId, pageId) => {
            if (!interactionPolicy.canMutateStructure) return;
            const st = useDiagramStore.getState();
            if (st.participants.some(p => p.id === nodeId)) {
              st.updateParticipant(nodeId, { linkedPageId: pageId });
            } else {
              st.linkNodeToPage(nodeId, pageId);
            }
            setLinkPageForNodeId(null);
            setIsPageManagerOpen(false);
            showToast(`Linked to page`);
          }}
          onAddPage={(diagramType) => {
            if (!interactionPolicy.canMutateStructure) return;
            const st = useDiagramStore.getState();
            const newId = st.addPage(undefined, diagramType, diagramType === 'sequence' ? undefined : activeKit.id);
            if (linkPageForNodeId) {
              if (st.participants.some(p => p.id === linkPageForNodeId)) {
                st.updateParticipant(linkPageForNodeId, { linkedPageId: newId });
              } else {
                st.linkNodeToPage(linkPageForNodeId, newId);
              }
              setLinkPageForNodeId(null);
              setIsPageManagerOpen(false);
              switchPageWithHistory(newId);
              showToast('New page created and linked');
            } else {
              switchPageWithHistory(newId);
            }
          }}
          onRenamePage={(id, name) => { if (!isViewOnly) useDiagramStore.getState().renamePage(id, name); }}
          onDeletePage={(id) => { if (!isViewOnly) handleDeletePage(id); }}
          onSetPageDiagramType={isViewOnly ? undefined : ((id, type) => useDiagramStore.getState().setPageDiagramType(id, type))}
          palette={palette}
        />
      )}

      {/* ── Group breadcrumb (Blender-style nav) ─────────────── */}
      {/* Show breadcrumb when inside a group (either legacy groupNavStack or new graphNavStack) */}
      {(isInsideGroup || graphNavStack.length > 0) && (
        <GroupBreadcrumb
          graphNavStack={graphNavStack}
          exitGraph={exitGraph}
          palette={palette}
          glass={glass}
        />
      )}

      {/* ── Status bar ────────────────────────────────────────── */}
      {!(diagramType === 'sequence' && (stickyMode.bottomPinned || viewport.scale > 1.12)) && (
        <StatusBar
          palette={palette}
          glass={glass}
          diagramType={diagramType}
          nodeCount={nodes.length}
          edgeCount={edges.length}
          participantCount={participants.length}
          messageCount={messages.length}
          selectedCount={selectedItems.length}
          zoomScale={viewport.scale}
          canUndo={canUndo()}
          canRedo={canRedo()}
        />
      )}

      {/* ── Connection hint ───────────────────────────────────── */}
      <ConnectionHint isConnecting={connectionState.isConnecting} />

      {!isViewOnly && !emptyStateDismissed && canvasDrawTool === 'none' && !canvasStampType && !isFrameDrawMode && canvasConnectMode === 'none' && nodes.length === 0 && participants.length === 0 && (
        <EmptyState
          palette={palette}
          glass={glass}
          onStartC4={() => handleStartC4Workspace()}
          onPasteMermaid={() => setIsMermaidModalOpen(true)}
          onBlank={() => setEmptyStateDismissed(true)}
          onPickKit={(kit) => {
            snapshotBeforeMutation();
            setPageKit(activePage, kit);
            setEmptyStateDismissed(true);
          }}
        />
      )}

      <CommandPalette
        palette={palette}
        glass={glass}
        isViewOnly={isViewOnly}
        windowSize={windowSize}
        onStartC4={() => handleStartC4Workspace()}
        onPasteMermaid={() => setIsMermaidModalOpen(true)}
        onGroupSelected={handleGroupSelected}
      />

      {/* ── Pan hint ──────────────────────────────────────────── */}
      {spaceHeld && !isPanning && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className="rounded-xl px-4 py-2 text-xs shadow-xl" style={{ ...glass, color: palette.panelSubText }}>
            Click + drag to pan
          </div>
        </div>
      )}

      {/* ── Quick node creation menu ─────────────────────────── */}
      {quickNodeCreation?.show && !isViewOnly && (
        <div ref={quickNodeMenuRef} className="fixed z-[200] min-w-[200px] rounded-xl shadow-2xl border overflow-hidden backdrop-blur-md"
          style={{ left: quickNodeCreation.screenX, top: quickNodeCreation.screenY, ...glass }}>
          <div className="py-1">
            <div className="px-4 py-1.5 text-xs uppercase tracking-wider" style={{ color: palette.panelSubText }}>Create & Connect</div>
            {kitQuickCreateTypes(activeKit.id).slice(0, 10).map(type => {
              const meta = NODE_TYPES[type as keyof typeof NODE_TYPES];
              if (!meta) return null;
              return (
                <button key={type}
                  className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                  style={{ color: palette.panelText }}
                  onClick={() => { handleQuickNodeCreation(type); }}>
                  {meta.label}
                </button>
              );
            })}
            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
            <button className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelSubText }}
              onClick={() => { useDiagramStore.getState().cancelConnection(); setQuickNodeCreation(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Chord hint ────────────────────────────────────────── */}
      {chordHint && (
        <ChordHint palette={palette} glass={glass} hint={chordHint} />
      )}

      {/* ── Frame draw mode hint ────────────────────────────── */}
      <FrameDrawHint
        tool={canvasDrawTool}
        stampType={canvasStampType}
        onExit={() => {
          useUIStore.getState().clearArmedTools();
          setCanvasDrawTool('none');
          setCanvasStampType(null);
          setIsFrameDrawMode(false);
          frameDrawStart.current = null;
          setFrameDrawRect(null);
          setMarkupStroke(null);
        }}
      />

      {/* ── Discovery mode overlay ──────────────────────────── */}
      <DiscoveryOverlay
        palette={palette}
        glass={glass}
        isActive={discoveryMode}
        nodes={nodes}
        groups={groups}
        layers={layers}
        viewport={viewport}
        windowSize={windowSize}
        catalog={catalog}
        messages={messages}
        participants={participants}
      />

      {/* ── Legacy discovery tooltips (kept for compatibility) ── */}
      {false && discoveryMode && nodes
        .filter(node => {
          const layer = layers.find(l => l.id === (node.layerId ?? 'default'));
          if (!(layer?.visible ?? true)) return false;
          if (node.parentGroupId) {
            const grp = groups.find(g => g.id === node.parentGroupId);
            if (grp?.collapsed) return false;
          }
          return true;
        })
        .map(node => {
          const sx = node.position.x * viewport.scale + viewport.x;
          const sy = node.position.y * viewport.scale + viewport.y;
          // Skip nodes off-screen
          if (sx > windowSize.width + 50 || sy > windowSize.height + 50 || sx < -250 || sy < -150) return null;
          const accentColor = node.data?.color ?? '#6366f1';
          return (
            <div
              key={`disc-${node.id}`}
              className="fixed z-[400] rounded-lg shadow-xl border backdrop-blur-md px-2.5 py-2 min-w-[120px] max-w-[180px] pointer-events-none"
              style={{ left: sx, top: sy - 50, ...glass }}
            >
              <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: accentColor }}>
                {node.type}
              </div>
              <div className="text-xs font-semibold truncate" style={{ color: palette.panelText }}>{node.label}</div>
              {node.meta?.owner && (
                <div className="text-[10px] truncate" style={{ color: palette.panelSubText }}>{node.meta.owner}</div>
              )}
              {node.meta?.status && (
                <div className="text-[10px] capitalize" style={{ color: STATUS_COLORS[node.meta.status] ?? '#94a3b8' }}>{node.meta.status}</div>
              )}
            </div>
          );
        })
      }

      {/* ── Context menu ──────────────────────────────────────── */}
      <ContextMenu
        contextMenu={contextMenu}
        onClose={closeContextMenu}
        palette={palette}
        glass={glass}
        defaultRoutingMode={pageRoutingMode ?? 'bezier'}
        nodes={nodes}
        edges={edges}
        groups={groups}
        layers={layers}
        participants={participants}
        participantGroups={participantGroups}
        messages={messages}
        selectedItems={selectedItems}
        onRotateGeneralShape={handleRotateGeneralShape}
        onAddSocket={handleAddSocketWithRename}
        onRotateNodeDirection={handleRotateNodeDirection}
        onRenameNode={handleNodeRename}
        onViewNodeInfo={(id) => { usedViaPointer('info-panel'); setInfoPanelNodeId(id); }}
        onChangeNodeType={(nodeId, newType) => {
          const ctxNode = nodes.find((n) => n.id === nodeId);
          if (!ctxNode || newType === ctxNode.type) return;
          const hasEndpoints = ctxNode.type === 'api' && (ctxNode.data?.endpoints as string[] | undefined)?.length;
          if (hasEndpoints) {
            if (!window.confirm(`This node has API endpoints defined. Switching to "${newType}" will remove them. Continue?`)) return;
          }
          snapshotBeforeMutation();
          const typeMeta = NODE_TYPES[newType as keyof typeof NODE_TYPES];
          updateNode(nodeId, {
            type: newType,
            data: { ...ctxNode.data, color: typeMeta.color, endpoints: undefined },
          });
        }}
        onEnterGraph={(id) => { usedViaPointer('enter-group'); enterGraph(id); }}
        onSwitchPage={switchPageWithHistory}
        onUnlinkPage={(nodeId) => useDiagramStore.getState().unlinkNodePage(nodeId)}
        onLinkPage={(nodeId) => { setLinkPageForNodeId(nodeId); setIsPageManagerOpen(true); }}
        onCreateChildView={(nodeId) => {
          snapshotBeforeMutation();
          const pageId = createChildView(nodeId);
          if (pageId) switchPageWithHistory(pageId);
        }}
        onCreateGroup={(ids) => { usedViaPointer('group-nodes'); createGroup(ids); }}
        onDissolveGroup={dissolveGroup}
        onSetNodeLayer={setNodeLayer}
        onRemoveNode={(id) => { usedViaPointer('delete-selection'); removeNode(id); }}
        onEditEdgeLabel={(edgeId) => setInlineEdgeEdit({ edgeId })}
        onAddEdgeAnchor={handleAddAnchor}
        onClearEdgeAnchors={(edgeId) => updateEdge(edgeId, { waypoints: [] })}
        onToggleEdgeRoutingMode={(edgeId, current) => {
          const next = current === 'bezier' ? 'orthogonal' : 'bezier';
          snapshotBeforeMutation();
          updateEdge(edgeId, { routingMode: next });
        }}
        onToggleEdgeAnimation={(edgeId, current) => { usedViaPointer('toggle-animation'); updateEdge(edgeId, { animated: !current }); }}
        onRemoveEdge={(id) => { usedViaPointer('delete-selection'); removeEdge(id); }}
        onRenameGroup={handleGroupRename}
        onToggleGroupCollapse={toggleGroupCollapse}
        onUngroupNodes={ungroupNodes}
        onOpenGroupColorPicker={(groupId) => { usedViaPointer('change-color'); setColorPicker({ show: true, targetNodeIds: [], targetGroupIds: [groupId] }); }}
        onViewParticipantInfo={(id) => setInfoPanelParticipantId(id)}
        onRenameParticipant={(id) => setInlineEdit({ nodeId: id, field: 'label' })}
        onGroupParticipants={groupParticipants}
        onSetParticipantLayer={setParticipantLayer}
        onRemoveParticipant={(id) => { usedViaPointer('delete-selection'); removeParticipant(id); }}
        onToggleParticipantGroupCollapse={toggleParticipantGroupCollapse}
        onPromptRenameParticipantGroup={(groupId) => {
          setInlineEdit({ nodeId: groupId, field: 'label' });
        }}
        onUngroupParticipants={ungroupParticipants}
        onViewMessageInfo={(id) => setInfoPanelMessageId(id)}
        onEditMessageLabel={(id) => setInlineEdgeEdit({ edgeId: id })}
        onUpdateMessageType={(id, type) => updateMessage(id, { type })}
        onUpdateMessageColor={(id, color) => updateMessage(id, { color })}
        onCopyMessage={(message) => { usedViaPointer('copy'); setSeqClipboard({ ...message }); setIsCut(false); }}
        onCutMessage={(message) => {
          usedViaPointer('cut');
          setSeqClipboard({ ...message });
          setIsCut(true);
          snapshotBeforeMutation();
          removeMessage(message.id);
          clearSelection();
        }}
        onRemoveMessage={(id) => { usedViaPointer('delete-selection'); removeMessage(id); }}
        snapshotBeforeMutation={snapshotBeforeMutation}
      />


      {/* ── Sequence message tooltip ─────────────────────────── */}
      {seqMsgTooltip && (() => {
        const ttMsg = messages.find(m => m.id === seqMsgTooltip.msgId);
        if (!ttMsg?.tooltip) return null;
        return (
          <div
            className="fixed z-[500] rounded-xl shadow-2xl border backdrop-blur-md p-3 min-w-[160px] max-w-[240px] pointer-events-none"
            style={{ left: seqMsgTooltip.screenX + 14, top: seqMsgTooltip.screenY - 14, ...glass }}
          >
            <div className="text-xs leading-snug whitespace-pre-wrap" style={{ color: palette.panelSubText }}>{ttMsg.tooltip}</div>
          </div>
        );
      })()}

      {/* ── Tooltip (hover + I key) ───────────────────────────── */}
      {tooltip && (() => {
        const ttNode = nodes.find(n => n.id === tooltip.nodeId);
        if (!ttNode) return null;
        const accentColor = ttNode.data?.color ?? '#6366f1';
        return (
          <div
            className="fixed z-[500] rounded-xl shadow-2xl border backdrop-blur-md p-3 min-w-[160px] max-w-[220px] pointer-events-none"
            style={{ left: tooltip.screenX + 14, top: tooltip.screenY - 14, ...glass }}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: accentColor }}>
              {ttNode.type}
            </div>
            <div className="text-sm font-semibold mb-1" style={{ color: palette.panelText }}>{ttNode.label}</div>
            {ttNode.meta?.description && (
              <div className="text-xs leading-snug mb-1" style={{ color: palette.panelSubText }}>{ttNode.meta.description}</div>
            )}
            {ttNode.meta?.owner && (
              <div className="text-xs" style={{ color: palette.panelSubText }}>Owner: {ttNode.meta.owner}</div>
            )}
            {ttNode.meta?.status && (
              <div className="text-xs capitalize mt-0.5" style={{ color: STATUS_COLORS[ttNode.meta.status] ?? '#94a3b8' }}>{ttNode.meta.status}</div>
            )}
            <div className="text-[10px] mt-2 pt-2 border-t" style={{ color: palette.panelBorder, borderColor: palette.panelBorder }}>
              Press I again for full details · Esc to close
            </div>
          </div>
        );
      })()}

      {/* ── Inline edit overlay ───────────────────────────────── */}
      {inlineEdit && (() => {
        // Check if it's a participant
        const editParticipant = participants.find(p => p.id === inlineEdit.nodeId);
        if (editParticipant && inlineEdit.field === 'label') {
          const sx = (editParticipant.x + 36) * viewport.scale + viewport.x;
          const sy = (40 + 0) * viewport.scale + viewport.y; // HEADER_Y=40
          return (
            <input
              ref={inlineEditInputRef}
              autoFocus
              defaultValue={editParticipant.label}
              style={{
                position: 'fixed',
                zIndex: 9999,
                background: 'transparent',
                border: 'none',
                borderBottom: `1.5px solid ${editParticipant.color}`,
                outline: 'none',
                boxShadow: 'none',
                padding: 0,
                margin: 0,
                left: sx,
                top: sy,
                width: (140 - 42) * viewport.scale,
                height: 44 * viewport.scale,
                fontSize: Math.max(11, 13 * viewport.scale),
                fontWeight: 600,
                color: palette.nodeText,
                fontFamily: FONT_FAMILY,
                caretColor: editParticipant.color,
                textAlign: 'left',
                lineHeight: `${44 * viewport.scale}px`,
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') setInlineEdit(null);
              }}
              onBlur={e => {
                const val = e.target.value.trim();
                if (val) updateParticipant(inlineEdit.nodeId, { label: val });
                setInlineEdit(null);
              }}
            />
          );
        }

        const editParticipantGroup = participantGroups.find(g => g.id === inlineEdit.nodeId);
        if (editParticipantGroup && inlineEdit.field === 'label') {
          const children = participants.filter(p => editParticipantGroup.childParticipantIds.includes(p.id));
          const collapsed = editParticipantGroup.collapsed;
          const avgX = children.length > 0
            ? children.reduce((sum, p) => sum + p.x, 0) / children.length
            : 0;
          const minX = children.length > 0
            ? Math.min(...children.map(p => p.x)) - 20
            : 0;
          const sx = collapsed
            ? (avgX + 36) * viewport.scale + viewport.x
            : (minX + 8) * viewport.scale + viewport.x;
          const sy = collapsed
            ? 40 * viewport.scale + viewport.y
            : 20 * viewport.scale + viewport.y;
          return (
            <input
              ref={inlineEditInputRef}
              autoFocus
              defaultValue={editParticipantGroup.label}
              style={{
                position: 'fixed',
                zIndex: 9999,
                background: collapsed ? 'transparent' : palette.panelBg,
                border: collapsed ? 'none' : `1.5px solid ${editParticipantGroup.color}`,
                borderBottom: collapsed ? `1.5px solid ${editParticipantGroup.color}` : undefined,
                borderRadius: collapsed ? 0 : 4,
                outline: 'none',
                boxShadow: 'none',
                padding: collapsed ? 0 : '2px 6px',
                margin: 0,
                left: sx,
                top: sy,
                width: collapsed ? (140 - 42) * viewport.scale : Math.max(80, 112 * viewport.scale),
                height: collapsed ? 44 * viewport.scale : 22 * viewport.scale,
                fontSize: Math.max(10, (collapsed ? 13 : 10) * viewport.scale),
                fontWeight: 600,
                color: collapsed ? palette.nodeText : '#ffffff',
                fontFamily: FONT_FAMILY,
                caretColor: editParticipantGroup.color,
                textAlign: 'left',
                lineHeight: `${(collapsed ? 44 : 22) * viewport.scale}px`,
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') setInlineEdit(null);
              }}
              onBlur={e => {
                const val = e.target.value.trim();
                if (val) {
                  snapshotBeforeMutation();
                  updateParticipantGroup(inlineEdit.nodeId, { label: val });
                }
                setInlineEdit(null);
              }}
            />
          );
        }

        const node = nodes.find(n => n.id === inlineEdit.nodeId);
        if (!node) return null;
        const nIn = node.sockets.filter(s => s.type === 'input').length;
        const nOut = node.sockets.filter(s => s.type === 'output').length;
        const epCount = (node.type === 'api' && Array.isArray(node.data?.endpoints)) ? node.data.endpoints.length : 0;
        const baseH = calcNodeHeight(nIn, nOut, epCount);

        const accentColor = node.data?.color ?? '#6366f1';

        if (inlineEdit.field === 'label') {
          if (node.type === 'text' || node.type === 'note') {
            const pad = node.type === 'note' ? 10 : 4;
            const fontSize = node.type === 'note' ? 13 : 16;
            const sx = (node.position.x + pad) * viewport.scale + viewport.x;
            const sy = (node.position.y + (node.type === 'note' ? 12 : 4)) * viewport.scale + viewport.y;
            return (
              <textarea
                ref={inlineEditTextareaRef}
                autoFocus
                defaultValue={node.label}
                style={{
                  position: 'fixed',
                  zIndex: 9999,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  padding: 0,
                  margin: 0,
                  left: sx,
                  top: sy,
                  width: Math.max(40, (node.size.width - pad * 2) * viewport.scale),
                  height: Math.max(24, (node.size.height - pad * 2) * viewport.scale),
                  fontSize: Math.max(11, fontSize * viewport.scale),
                  fontWeight: node.type === 'text' ? 500 : 400,
                  color: node.type === 'text' && node.data?.color && node.data.color !== '#e2e8f0'
                    ? String(node.data.color)
                    : palette.nodeText,
                  fontFamily: FONT_FAMILY,
                  caretColor: accentColor,
                  resize: 'none',
                  lineHeight: node.type === 'note'
                    ? `${18 * viewport.scale}px`
                    : `${22 * viewport.scale}px`,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') setInlineEdit(null);
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) e.currentTarget.blur();
                }}
                onBlur={e => {
                  const val = e.target.value.trim() || (node.type === 'note' ? 'Note' : 'Text');
                  if (node.type === 'text') {
                    const fitted = fitTextNodeSize(val, node.size.width);
                    updateNode(inlineEdit.nodeId, {
                      label: val,
                      size: { width: Math.max(node.size.width, fitted.width), height: fitted.height },
                    });
                  } else {
                    updateNode(inlineEdit.nodeId, { label: val });
                  }
                  setInlineEdit(null);
                }}
              />
            );
          }
          if (usesWrappedCenteredLabel(node.type)) {
            return (
              <InPlaceSketchLabel
                nodeId={node.id}
                nodeType={node.type}
                label={node.label}
                size={node.size}
                position={node.position}
                viewport={viewport}
                accentColor={accentColor}
                palette={palette}
                textareaRef={inlineEditTextareaRef}
                onFitSize={(id, next) => handleNodeResize(id, next.width, next.height)}
                onCommit={(id, val, next) => {
                  setInlineEdit(null);
                  updateNode(id, { label: val });
                  if (next.width !== node.size.width || next.height !== node.size.height) {
                    handleNodeResize(id, next.width, next.height);
                  }
                }}
                onCancel={() => setInlineEdit(null)}
              />
            );
          }
          const isShapedCentered = ['database', 'queue', 'diamond'].includes(node.type);
          const isActor = node.type === 'uml_actor';
          const labelCanvasX = isShapedCentered ? 8 : 14;
          const labelCanvasY = isActor ? node.size.height - 18 : isShapedCentered ? node.size.height / 2 - 6 : 26;
          const labelCanvasW = isShapedCentered ? node.size.width - 16 : node.size.width - 42;
          const labelCanvasH = 18;
          const sx = (node.position.x + labelCanvasX) * viewport.scale + viewport.x;
          const sy = (node.position.y + labelCanvasY) * viewport.scale + viewport.y;
          return (
            <input
              ref={inlineEditInputRef}
              autoFocus
              defaultValue={node.label}
              style={{
                position: 'fixed',
                zIndex: 9999,
                background: 'transparent',
                border: 'none',
                borderBottom: `1.5px solid ${accentColor}`,
                outline: 'none',
                boxShadow: 'none',
                padding: 0,
                margin: 0,
                left: sx,
                top: sy,
                width: labelCanvasW * viewport.scale,
                height: labelCanvasH * viewport.scale,
                fontSize: Math.max(11, (isActor ? 11 : 13) * viewport.scale),
                fontWeight: 600,
                color: palette.nodeText,
                fontFamily: FONT_FAMILY,
                caretColor: accentColor,
                textAlign: isShapedCentered ? 'center' : 'left',
                lineHeight: `${labelCanvasH * viewport.scale}px`,
                boxSizing: 'border-box',
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') setInlineEdit(null);
              }}
              onBlur={e => {
                updateNode(inlineEdit.nodeId, { label: e.target.value.trim() });
                setInlineEdit(null);
              }}
            />
          );
        }

        // description field — keeps a visible box since it overlays the desc area below the node
        const descStyle: React.CSSProperties = {
          position: 'fixed',
          zIndex: 9999,
          background: palette.nodeBody,
          border: `1px solid ${accentColor}80`,
          borderRadius: 4,
          color: palette.nodeText,
          fontFamily: FONT_FAMILY,
          outline: 'none',
          boxShadow: `0 0 0 2px ${accentColor}30`,
        };
        const sx = (node.position.x + 10) * viewport.scale + viewport.x;
        const sy = (node.position.y + baseH + 6) * viewport.scale + viewport.y;
        const descW = (NODE_WIDTH - 20) * viewport.scale;
        const descH = (DESC_H - 14) * viewport.scale;
        return (
          <textarea
            ref={inlineEditTextareaRef}
            autoFocus
            defaultValue={node.meta?.description ?? ''}
            style={{
              ...descStyle,
              left: sx,
              top: sy,
              width: descW,
              height: Math.max(50, descH),
              fontSize: Math.max(9, 10 * viewport.scale),
              padding: '4px 6px',
              resize: 'none',
              lineHeight: 1.5,
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') { setInlineEdit(null); e.preventDefault(); }
              if (e.key === 'Enter' && e.ctrlKey) e.currentTarget.blur();
            }}
            onBlur={e => {
              const val = e.target.value.trim();
              updateNodeMeta(inlineEdit.nodeId, { description: val || undefined });
              // Recalculate height based on whether description was added/removed
              const newH = baseH + (val ? DESC_H : 0);
              updateNode(inlineEdit.nodeId, { size: { width: node.size.width, height: newH } });
              if (node.parentGroupId) recalcGroupBounds(node.parentGroupId);
              setInlineEdit(null);
            }}
          />
        );
      })()}

      {/* ── Inline edge/message label edit overlay ─────────────────────── */}
      {inlineEdgeEdit && (() => {
        // Check if it's a sequence message
        const seqMsg = messages.find(m => m.id === inlineEdgeEdit.edgeId);
        if (seqMsg) {
          const srcP = participants.find(p => p.id === seqMsg.sourceId);
          const tgtP = participants.find(p => p.id === seqMsg.targetId);
          if (!srcP || !tgtP) return null;
          const srcX = srcP.x + 70;
          const tgtX = tgtP.x + 70;
          const isSelfMsg = seqMsg.sourceId === seqMsg.targetId;
          const midX = isSelfMsg ? srcX + 50 : (srcX + tgtX) / 2;
          const screenX = midX * viewport.scale + viewport.x;
          const screenY = (isSelfMsg ? seqMsg.y - 18 : seqMsg.y - 20) * viewport.scale + viewport.y;
          return (
            <textarea
              autoFocus
              defaultValue={seqMsg.label}
              placeholder="Type message..."
              rows={1}
              style={{
                position: 'fixed',
                zIndex: 9999,
                background: palette.panelBg,
                border: `1.5px solid ${palette.edgeSelected}`,
                borderRadius: 6,
                outline: 'none',
                padding: '3px 10px',
                left: screenX - 200,
                top: screenY - 16,
                width: 400,
                maxWidth: '80vw',
                minHeight: 32,
                fontSize: Math.max(11, 12 * viewport.scale),
                fontWeight: 500,
                color: palette.panelText,
                fontFamily: FONT_FAMILY,
                caretColor: palette.edgeSelected,
                textAlign: 'center' as const,
                boxShadow: `0 4px 12px ${palette.edgeSelected}30`,
                resize: 'none',
                overflow: 'hidden',
                lineHeight: '1.4',
              }}
              onInput={e => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = `${Math.max(32, el.scrollHeight)}px`;
              }}
              onKeyDown={e => {
                // Ctrl+Enter, Alt+Enter, or Cmd+Enter → insert newline
                if (e.key === 'Enter' && (e.ctrlKey || e.altKey || e.metaKey)) {
                  return; // allow default textarea newline behaviour
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setInlineEdgeEdit(null);
                }
              }}
              onBlur={e => {
                const val = e.target.value.trim();
                updateMessage(inlineEdgeEdit.edgeId, { label: val });
                enforceSequenceMessageSpacing(inlineEdgeEdit.edgeId);
                setInlineEdgeEdit(null);
              }}
            />
          );
        }

        // Check if it's a fragment
        const frag = fragments.find(f => f.id === inlineEdgeEdit.edgeId);
        if (frag) {
          const fragPs = participants.filter(p => frag.participantIds.includes(p.id));
          const minX = fragPs.length > 0 ? Math.min(...fragPs.map(p => p.x)) - 20 : 100;
          const screenX = (minX + 90) * viewport.scale + viewport.x;
          const screenY = (frag.startY + 4) * viewport.scale + viewport.y;
          return (
            <input
              autoFocus
              defaultValue={frag.label}
              placeholder="Condition..."
              style={{
                position: 'fixed',
                zIndex: 9999,
                background: palette.panelBg,
                border: `1.5px solid ${palette.edgeSelected}`,
                borderRadius: 6,
                outline: 'none',
                padding: '2px 8px',
                left: screenX,
                top: screenY,
                width: 140,
                fontSize: Math.max(11, 12 * viewport.scale),
                fontWeight: 500,
                color: palette.panelText,
                fontFamily: FONT_FAMILY,
                caretColor: palette.edgeSelected,
                textAlign: 'left' as const,
                boxShadow: `0 4px 12px ${palette.edgeSelected}30`,
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') setInlineEdgeEdit(null);
              }}
              onBlur={e => {
                const val = e.target.value.trim();
                updateFragment(inlineEdgeEdit.edgeId, { label: val });
                setInlineEdgeEdit(null);
              }}
            />
          );
        }

        // Flow diagram edge
        const edge = edges.find(e => e.id === inlineEdgeEdit.edgeId);
        if (!edge) return null;

        // Editing an existing message — position at the message box
        const editingMsgId = inlineEdgeEdit.msgId;
        const existingMsg = editingMsgId ? (edge.messages ?? []).find(m => m.id === editingMsgId) : null;

        // Compute textarea screen position + size
        let taLeft: number, taTop: number, taMinW: number, taMinH: number;
        if (existingMsg && inlineEdgeEdit.canvasCX != null) {
          // Exact overlay: convert canvas-space message box to screen coords
          const screenCX = inlineEdgeEdit.canvasCX * viewport.scale + viewport.x;
          const screenCY = inlineEdgeEdit.canvasCY! * viewport.scale + viewport.y;
          const screenBoxW = Math.max((inlineEdgeEdit.canvasBoxW ?? 56) * viewport.scale, 80);
          const screenBoxH = (inlineEdgeEdit.canvasBoxH ?? 28) * viewport.scale;
          taLeft = screenCX - screenBoxW / 2;
          taTop = screenCY - screenBoxH / 2;
          taMinW = screenBoxW;
          taMinH = screenBoxH;
        } else {
          // New message: position at edge geometry midpoint
          const srcNode = nodes.find(n => n.id === edge.sourceNodeId);
          const tgtNode = nodes.find(n => n.id === edge.targetNodeId);
          if (!srcNode || !tgtNode) return null;
          const srcSock = srcNode.sockets.find(s => s.id === edge.sourceSocketId);
          const tgtSock = tgtNode.sockets.find(s => s.id === edge.targetSocketId);
          if (!srcSock || !tgtSock) return null;
          const geom = getEdgeGeometry(edge, srcNode, tgtNode, srcSock, tgtSock);
          if (!geom.labelPos) return null;
          const sx = geom.labelPos.x * viewport.scale + viewport.x;
          const sy = geom.labelPos.y * viewport.scale + viewport.y;
          taLeft = sx - 80;
          taTop = sy - 14;
          taMinW = 120;
          taMinH = 28;
        }

        const taFontSize = Math.max(11, 12 * viewport.scale);

        return (
          <textarea
            autoFocus
            defaultValue={existingMsg ? existingMsg.label : (edge.label ?? '')}
            placeholder={existingMsg ? 'Edit label…' : 'Type label…'}
            rows={1}
            style={{
              position: 'fixed',
              zIndex: 9999,
              background: palette.canvasBg,
              border: `1.5px solid ${palette.edgeSelected}`,
              borderRadius: 4,
              outline: 'none',
              padding: `${Math.round(6 * viewport.scale)}px ${Math.round(10 * viewport.scale)}px`,
              left: taLeft,
              top: taTop,
              minWidth: taMinW,
              minHeight: taMinH,
              width: 'auto',
              maxWidth: Math.max(taMinW, 400),
              fontSize: taFontSize,
              fontWeight: 500,
              color: palette.panelText,
              fontFamily: FONT_FAMILY,
              caretColor: palette.edgeSelected,
              textAlign: 'center' as const,
              boxShadow: `0 2px 12px ${palette.edgeSelected}40`,
              resize: 'none',
              overflow: 'hidden',
              lineHeight: `${17 * viewport.scale}px`,
            }}
            onInput={e => {
              const el = e.currentTarget;
              // Height: shrink then grow to fit content
              el.style.height = 'auto';
              el.style.height = `${Math.max(taMinH, el.scrollHeight)}px`;
              // Width: grow to fit the longest line, never narrower than the original box
              const charWidth = taFontSize * 0.62;
              const longestLine = el.value.split('\n').reduce((a, b) => a.length > b.length ? a : b, '');
              const needed = longestLine.length * charWidth + Math.round(10 * viewport.scale) * 2 + 4;
              el.style.width = `${Math.min(400, Math.max(taMinW, needed))}px`;
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey)) {
                // Any modifier+Enter → insert a literal newline at the cursor position
                e.preventDefault();
                const el = e.currentTarget;
                const start = el.selectionStart ?? el.value.length;
                const end = el.selectionEnd ?? el.value.length;
                el.value = el.value.slice(0, start) + '\n' + el.value.slice(end);
                el.selectionStart = el.selectionEnd = start + 1;
                el.dispatchEvent(new Event('input', { bubbles: true }));
                return;
              }
              // Plain Enter → commit
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') { e.preventDefault(); setInlineEdgeEdit(null); }
            }}
            onBlur={e => {
              const val = e.target.value;
              const trimmedVal = val.trim();
              if (existingMsg) {
                // Editing an existing message
                snapshotBeforeMutation();
                if (trimmedVal) {
                  const updated = (edge.messages ?? []).map(m =>
                    m.id === existingMsg.id ? { ...m, label: trimmedVal } : m
                  );
                  updateEdge(inlineEdgeEdit.edgeId, { messages: updated });
                } else {
                  // Empty label → remove the message
                  const updated = (edge.messages ?? []).filter(m => m.id !== existingMsg.id);
                  updateEdge(inlineEdgeEdit.edgeId, { messages: updated.length > 0 ? updated : undefined });
                }
              } else {
                if (!trimmedVal) { setInlineEdgeEdit(null); return; }
                // Adding a new message
                if (edge.messages && edge.messages.length > 0) {
                  const newMsg = { id: generateId('emsg'), label: trimmedVal, distanceOffset: 0.5 };
                  snapshotBeforeMutation();
                  updateEdge(inlineEdgeEdit.edgeId, { messages: [...edge.messages, newMsg] });
                } else {
                  updateEdge(inlineEdgeEdit.edgeId, { label: trimmedVal || undefined });
                }
              }
              setInlineEdgeEdit(null);
            }}
          />
        );
      })()}

      {/* ── Inline socket name edit overlay ─────────────────────── */}
      {inlineSocketEdit && (() => {
        const node = nodes.find(n => n.id === inlineSocketEdit.nodeId);
        if (!node) return null;
        const socket = node.sockets.find(s => s.id === inlineSocketEdit.socketId);
        if (!socket) return null;

        // Match the exact label geometry used in NodeComponent for true in-place editing.
        const isShaped = ['diamond', 'database', 'queue'].includes(node.type);
        const halfW = node.size.width / 2;
        const labelX = !isShaped
          ? (socket.type === 'input' ? 12 : halfW)
          : (socket.type === 'input' ? 12 : Math.max(12, node.size.width - 128));
        const labelY = socket.position.y - 6;
        const labelW = !isShaped
          ? (socket.type === 'input' ? halfW - 16 : halfW - 8)
          : Math.max(88, Math.min(120, node.size.width - 24));
        const labelAlign = socket.type === 'input' ? 'left' : 'right';
        const screenX = (node.position.x + labelX) * viewport.scale + viewport.x;
        const screenY = (node.position.y + labelY) * viewport.scale + viewport.y;
        const fieldW = Math.max(70, labelW * viewport.scale);
        const fieldH = Math.max(16, 14 * viewport.scale);
        const accent = socket.type === 'input' ? '#10b981' : '#8b5cf6';
        
        return (
          <input
            autoFocus
            defaultValue={socket.label}
            placeholder="Socket name..."
            onFocus={e => e.currentTarget.select()}
            style={{
              position: 'fixed',
              zIndex: 9999,
              background: 'transparent',
              border: 'none',
              borderBottom: `1.5px solid ${accent}`,
              outline: 'none',
              margin: 0,
              padding: 0,
              left: screenX,
              top: screenY,
              width: fieldW,
              height: fieldH,
              fontSize: Math.max(10, 10 * viewport.scale),
              fontWeight: 500,
              color: palette.panelText,
              fontFamily: FONT_FAMILY,
              lineHeight: `${fieldH}px`,
              caretColor: accent,
              textAlign: labelAlign,
              boxShadow: `0 2px 10px ${accent}25`,
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setInlineSocketEdit(null);
            }}
            onBlur={e => {
              const val = e.target.value.trim();
              if (val) {
                renameSocket(inlineSocketEdit.nodeId, inlineSocketEdit.socketId, val);
              }
              setInlineSocketEdit(null);
            }}
          />
        );
      })()}

      {/* ── Inline group/frame label edit overlay ────────────── */}
      {inlineGroupEdit && (() => {
        const grp = groups.find(g => g.id === inlineGroupEdit.groupId);
        if (!grp) return null;
        const screenX = grp.position.x * viewport.scale + viewport.x;
        const screenY = grp.position.y * viewport.scale + viewport.y - 2;
        const accentCol = grp.color ?? '#f59e0b';
        return (
          <input
            autoFocus
            defaultValue={grp.label}
            placeholder="Frame name…"
            style={{
              position: 'fixed',
              zIndex: 9999,
              background: palette.panelBg,
              border: `1.5px solid ${accentCol}`,
              borderRadius: 6,
              outline: 'none',
              padding: '3px 10px',
              left: screenX + 8,
              top: screenY + 6,
              width: Math.max(140, Math.min(280, grp.size.width * viewport.scale * 0.6)),
              fontSize: Math.max(11, 12 * viewport.scale),
              fontWeight: 600,
              color: palette.panelText,
              fontFamily: FONT_FAMILY,
              caretColor: accentCol,
              boxShadow: `0 4px 12px ${accentCol}40`,
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setInlineGroupEdit(null);
            }}
            onBlur={e => {
              const val = e.target.value.trim();
              if (val) { snapshotBeforeMutation(); updateGroup(inlineGroupEdit.groupId, { label: val }); }
              setInlineGroupEdit(null);
            }}
          />
        );
      })()}

      {/* ── Settings modal ────────────────────────────────────── */}
      <SettingsPanel
        palette={palette}
        glass={glass}
        isOpen={isSettingsOpen}
        theme={theme}
        iconStyle={iconStyle}
        colorPickerStyle={colorPickerStyle}
        autoOpenInfoOnHover={autoOpenInfoOnHover}
        socketLabelVisibility={socketLabelVisibility}
        defaultRoutingMode={defaultRoutingMode}
        pageName={activePageEntry?.name}
        pageRoutingMode={pageSettings?.routingMode}
        pageTextScale={pageSettings?.textScale}
        forceLayoutEdges={forceLayoutEdges}
        tintEdgesByDestination={tintEdgesByDestination ?? true}
        layoutDirection={layoutDirection ?? 'auto'}
        mermaidSubgraphMode={mermaidSubgraphMode ?? 'frames'}
        mermaidNodeStyle={mermaidNodeStyle ?? 'nodes'}
        generalShapesEnabled={generalShapesEnabled ?? false}
        stickyDrawColorEnabled={stickyDrawColorEnabled ?? false}
        lastDrawColor={lastDrawColor ?? null}
        sequenceMessageNumbering={sequenceMessageNumbering ?? true}
        sequenceMonochromeViewer={sequenceMonochromeViewer ?? false}
        sequenceMonochromePaper={sequenceMonochromePaper ?? 'white'}
        onClose={() => setIsSettingsOpen(false)}
        onThemeChange={setTheme}
        onIconStyleChange={setIconStyle}
        onColorPickerStyleChange={setColorPickerStyle}
        onAutoOpenInfoOnHoverChange={setAutoOpenInfoOnHover}
        onSocketLabelVisibilityChange={setSocketLabelVisibility}
        onDefaultRoutingModeChange={setDefaultRoutingMode}
        onPageRoutingModeChange={mode => setPageSettings(activePage, { routingMode: mode })}
        onPageTextScaleChange={scale => setPageSettings(activePage, { textScale: scale })}
        onForceLayoutEdgesChange={setForceLayoutEdges}
        onTintEdgesByDestinationChange={setTintEdgesByDestination}
        onLayoutDirectionChange={setLayoutDirection}
        onMermaidSubgraphModeChange={setMermaidSubgraphMode}
        onMermaidNodeStyleChange={setMermaidNodeStyle}
        onGeneralShapesEnabledChange={setGeneralShapesEnabled}
        onStickyDrawColorEnabledChange={setStickyDrawColorEnabled}
        onSequenceMessageNumberingChange={setSequenceMessageNumbering}
        onSequenceMonochromeViewerChange={setSequenceMonochromeViewer}
        onSequenceMonochromePaperChange={setSequenceMonochromePaper}
        onExportSettings={() => {
          const blob = new Blob([exportSettings()], { type: 'application/json' });
          const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: 'velostruct-settings.json' });
          a.click(); URL.revokeObjectURL(a.href);
        }}
        onImportSettings={() => settingsFileRef.current?.click()}
        onClearDiagram={() => { snapshotBeforeMutation(); clearDiagram(); }}
        onResetViewport={() => updateViewport({ x: 0, y: 0, scale: Math.max(dynamicMinScale, 1) })}
      />

      {/* ── Feedback modal ──────────────────────────────────────── */}
      <FeedbackPanel
        palette={palette}
        glass={glass}
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        getDiagramJson={() => saveToJSON({ nodes, edges, groups, layers, viewport, catalog, pages, activePage, graphs })}
      />

      {/* ── AI Chat Panel ──────────────────────────────────────── */}
      <AIChatPanel palette={palette} glass={glass} />

      <input
        ref={settingsFileRef}
        type="file" accept=".json" className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = ev => { importSettings(ev.target?.result as string); };
          reader.readAsText(file);
          e.target.value = '';
        }}
      />

      {/* ── Dev State Viewer ───────────────────────────────────── */}
      {showLocalDevTools && (
        <DevStateViewer
          palette={palette}
          glass={glass}
          isOpen={isDevStateViewerOpen}
          state={{
            nodes,
            edges,
            groups,
            pages,
            participants,
            messages,
            fragments,
            layers,
            viewport,
            selectedItems,
            connectionState,
            groupNavStack,
            sequencePerfHistory,
          }}
          onClose={() => setIsDevStateViewerOpen(false)}
        />
      )}

      {/* ── Project Manager Modal ────────────────────────────── */}
      <ProjectManager
        isOpen={isProjectManagerOpen}
        onClose={() => setIsProjectManagerOpen(false)}
        palette={palette}
        glass={glass}
      />

      <PngExportModal
        isOpen={isPngExportModalOpen}
        isExporting={isPngExporting}
        pageCount={pages.length}
        includeAllPages={exportAllPagesAsPng}
        onIncludeAllPagesChange={setExportAllPagesAsPng}
        showMonochromeOption={diagramType === 'sequence'}
        monochrome={exportPngMonochrome}
        onMonochromeChange={setExportPngMonochrome}
        onClose={() => {
          if (isPngExporting) return;
          setIsPngExportModalOpen(false);
        }}
        onConfirm={handleConfirmPngExport}
        palette={palette}
        glass={glass}
      />

      {/* ── Chord Reference Modal ─────────────────────────────── */}
      <ChordReferenceModal
        isOpen={isChordModalOpen}
        onClose={() => setIsChordModalOpen(false)}
        palette={palette}
        glass={glass}
      />

      {/* ── Distraction-Free mode escape hint ─────────────────── */}
      {isDistractionFree && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[400] px-4 py-2 rounded-xl text-xs select-none pointer-events-none"
          style={{ background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(8px)' }}
        >
          Distraction-free — press <kbd className="px-1 rounded border border-white/20 text-[10px]">t+f</kbd> to exit
        </div>
      )}

      {/* ── Color Picker Modal ───────────────────────────────── */}
      {colorPicker?.show && (
        <ColorPickerModal
          targetNodeIds={colorPicker.targetNodeIds}
          targetGroupIds={colorPicker.targetGroupIds}
          targetEdgeIds={colorPicker.targetEdgeIds}
          targetParticipantIds={colorPicker.targetParticipantIds}
          targetMessageIds={colorPicker.targetMessageIds}
          avoidRect={colorPicker.avoidRect}
          windowSize={windowSize}
          palette={palette}
          snapshotBeforeMutation={snapshotBeforeMutation}
          onClose={() => setColorPicker(null)}
        />
      )}

      {/* ── Toast Notification ────────────────────────────────── */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 z-[300] pointer-events-none" style={{ transform: 'translateX(-50%)' }}>
          <div className="rounded-xl px-5 py-2.5 shadow-xl border backdrop-blur-md" style={{ backgroundColor: palette.panelBg, borderColor: palette.panelBorder }}>
            <span className="text-xs font-medium" style={{ color: palette.panelText }}>{toast.message}</span>
          </div>
        </div>
      )}

      {/* ── Mermaid Export/Import Modal ───────────────────────── */}
      <MermaidModal
        palette={palette}
        glass={glass}
        isOpen={isMermaidModalOpen}
        currentMermaidExport={(() => {
          const st = useDiagramStore.getState();
          return diagramType === 'sequence'
            ? diagramToMermaidSequence(participants, messages, fragments, participantGroups)
            : diagramToMermaid(nodes, edges, groups, st.graphs);
        })()}
        onClose={() => setIsMermaidModalOpen(false)}
        onImport={async (code) => {
          snapshotBeforeMutation();
          // Import lays the graph out before it writes to the store, so the
          // result only arrives once ELK has answered.
          const result = await invoke('importFromMermaid', { mmd: code });
          if (isCommandError(result)) {
            alert('Import Error: ' + result.error);
          }
        }}
        onExport={() => {
          const st = useDiagramStore.getState();
          return diagramType === 'sequence'
            ? diagramToMermaidSequence(participants, messages, fragments, participantGroups)
            : diagramToMermaid(nodes, edges, groups, st.graphs);
        }}
      />

      <ShortcutNudge palette={palette} glass={glass} hidden={isViewOnly} />
      <TutorialCoach palette={palette} glass={glass} isViewOnly={isViewOnly} />
    </div>
  );
}

export default App;

